import React, { useRef, useState, useEffect, useMemo, useCallback, useDeferredValue } from 'react';
import { io as socketIOClient } from 'socket.io-client';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import * as XLSX from 'xlsx';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './CodificarPage.css';
import { API_BASE_URL, SOCKET_URL } from './config';
import { hasUserRole } from './userRoles';
import ValeManualForm from './ValeManualForm';
import { buildValeManualRow, guardarValeManual } from './valeManualUtils';

ModuleRegistry.registerModules([AllCommunityModule]);

const normalizeKeyPart = (value) => (value ?? '').toString().trim().toUpperCase();

const buildSiniestroItemKey = (siniestro, item) => {
  const siniestroKey = normalizeKeyPart(siniestro);
  const itemKey = normalizeKeyPart(item);
  if (!siniestroKey && !itemKey) return '';
  return `${siniestroKey}|||${itemKey}`;
};

const extractSiniestro = (row) => row?.SINIESTRO ?? row?.siniestro ?? row?.Siniestro ?? '';
const extractItem = (row) => row?.ITEM ?? row?.item ?? row?.Item ?? '';
const extractOc = (row) => row?.OC ?? row?.oc ?? row?.Oc ?? '';
const extractBackOrder = (row) => row?.BACK_ORDER ?? row?.back_order ?? '';
const extractAseguradora = (row) => row?.ASEGURADORA ?? row?.aseguradora ?? '';
const extractEstatus = (row) => row?.ESTATUS ?? row?.estatus ?? '';

const isValeManualRow = (row) =>
  normalizeKeyPart(extractEstatus(row)).includes('VALE MANUAL');

const isQualitasRow = (row) => {
  const a = normalizeKeyPart(extractAseguradora(row));
  return a.includes('QUALITAS') || a.includes('XNUUP') || a.includes('XNUUB');
};
const isMultiasistenciaRow = (row) => normalizeKeyPart(extractAseguradora(row)).includes('MULTIASISTENCIA');
const buildValeManualKey = (row) => {
  const s=normalizeKeyPart(extractSiniestro(row));
  const i=normalizeKeyPart(extractItem(row));
  const o=normalizeKeyPart(extractOc(row));
  return s && i && o ? `${s}|||${i}|||${o}` : '';
};

const buildQualitasKey = (row) => {
  const s=normalizeKeyPart(extractSiniestro(row));
  const i=normalizeKeyPart(extractItem(row));
  const b=normalizeKeyPart(extractBackOrder(row));
  return s && i && b ? `${s}|||${i}|||${b}` : '';
};

const extractPedido = (row) => {
  const aseguradora = (row?.ASEGURADORA ?? row?.aseguradora ?? row?.Aseguradora ?? '').toString().toUpperCase();
  const cotizacion = row?.COTIZACION ?? row?.cotizacion ?? row?.Cotizacion ?? '';
  if (aseguradora.includes('MULTIASISTENCIA') && normalizeKeyPart(cotizacion)) {
    return cotizacion;
  }
  return row?.PEDIDO ?? row?.pedido ?? row?.Pedido ?? '';
};

const extractPedidoReal = (row) =>
  row?.PEDIDO ?? row?.pedido ?? row?.Pedido ?? '';

const isMultiDuplicadoRow = (row) =>
  normalizeKeyPart(extractAseguradora(row)).includes('MULTIASISTENCIA');

const isSameCodificarRow = (a, b) =>
  a === b || (
    a?.id != null &&
    b?.id != null &&
    String(a.id) === String(b.id)
  );

const hasMultiPedidoConflict = (row, candidate) => {
  if (!isMultiDuplicadoRow(row) || !isMultiDuplicadoRow(candidate)) return false;

  const rowKey = buildPedidoItemKey(extractPedido(row), extractItem(row));
  const candidateKey = buildPedidoItemKey(
    extractPedido(candidate),
    extractItem(candidate)
  );

  if (!rowKey || rowKey !== candidateKey) return false;

  const pedidoA = normalizeKeyPart(extractPedidoReal(row));
  const pedidoB = normalizeKeyPart(extractPedidoReal(candidate));

  return !pedidoA || !pedidoB || pedidoA === pedidoB;
};

const formatDateInputToExcelString = (value) => {
  if (!value) return '';
  const parts = String(value).split('-');
  if (parts.length !== 3) return value;
  const [anio, mes, dia] = parts;
  return `${dia}/${mes}/${anio} 00:00:00`;
};

const buildPedidoItemKey = (pedido, item) => {
  const pedidoKey = normalizeKeyPart(pedido);
  const itemKey = normalizeKeyPart(item);
  if (!pedidoKey && !itemKey) return '';
  return `${pedidoKey}|||${itemKey}`;
};

function excelDateToJSDate(serial) {
  if (typeof serial !== 'number' || Number.isNaN(serial)) return '';

  const milliseconds = Math.round((serial - 25569) * 86400 * 1000);
  const date = new Date(milliseconds);
  if (Number.isNaN(date.getTime())) return '';

  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds()
  );
}

function parseFechaDDMMYYYY(fechaStr) {
  if (!fechaStr) return null;
  const [fecha, hora] = fechaStr.split(' ');
  if (!fecha) return null;
  const [dia, mes, anio] = fecha.split('/');
  if (!dia || !mes || !anio) return null;
  let h = 0, m = 0, s = 0;
  if (hora) {
    [h, m, s] = hora.split(':').map(Number);
  }
  // Mes en JS es 0-indexado
  return new Date(Number(anio), Number(mes) - 1, Number(dia), h, m, s);
}

const formatFecha = params => {
  if (!params.value) return '';
  // Si es string en formato dd/mm/yyyy, parsea correctamente
  if (typeof params.value === 'string' && params.value.match(/^\d{2}\/\d{2}\/\d{4}/)) {
    const date = parseFechaDDMMYYYY(params.value);
    if (date && !isNaN(date.getTime())) {
      return date.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
        ' ' +
        date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
    }
  }
  // Si es string numérico (Excel), conviértelo a número
  let value = params.value;
  if (typeof value === 'string' && !isNaN(value) && value.trim() !== '') {
    value = Number(value);
  }
  if (typeof value === 'number' && !isNaN(value)) {
    const date = excelDateToJSDate(value);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
        ' ' +
        date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
    }
  }
  // Si es string en otro formato, intenta parsear normal
  const date = new Date(params.value);
  if (!isNaN(date.getTime())) {
    return date.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
      ' ' +
      date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  }
  return params.value;
};

const BASE_COLUMN_DEFS = [
  { headerName: '', field: 'checked', checkboxSelection: true, headerCheckboxSelection: true, width: 30, pinned: 'left' },
  { headerName: "ASEGURADORA", field: "ASEGURADORA", width: 180 },
  { headerName: "COTIZACION", field: "COTIZACION", width: 120 },
  { headerName: "PEDIDO", field: "PEDIDO", width: 120 },
  { headerName: "FECHA COTIZACION", field: "FECHA_COTIZACION", width: 160, valueFormatter: formatFecha },
  { headerName: "FECHA PEDIDO", field: "FECHA_PEDIDO", width: 180, valueFormatter: formatFecha },
  { headerName: "PROMESA DE ENTREGA", field: "PROMESA_DE_ENTREGA", width: 150, valueFormatter: formatFecha },
  { headerName: "FECHA CONFIRMACION DE PIEZA", field: "FECHA_CONFIRMACION_DE_PIEZA", width: 180, valueFormatter: formatFecha },
  { headerName: "FECHA CANCELACION DE PIEZA", field: "FECHA_CANCELACION_DE_PIEZA", width: 180, valueFormatter: formatFecha },
  { headerName: "FECHA FACTURACION DE PIEZA", field: "FECHA_FACTURACION_DE_PIEZA", width: 180, valueFormatter: formatFecha },
  { headerName: "FECHA ENTREGA DE PIEZA", field: "FECHA_ENTREGA_DE_PIEZA", width: 180, valueFormatter: formatFecha },
  { headerName: "FECHA RECEPCION DE PIEZA", field: "FECHA_RECEPCION_DE_PIEZA", width: 180, valueFormatter: formatFecha },
  { headerName: "DIAS PROMESA", field: "DIAS_PROMESA", width: 120 },
  { headerName: "DIAS ENTREGADO1", field: "DIAS_ENTREGADO1", width: 220 },
  { headerName: "DIAS ENTREGADO2", field: "DIAS_ENTREGADO2", width: 250 },
  { headerName: "DIAS RECIBIDO1", field: "DIAS_RECIBIDO1", width: 220 },
  { headerName: "DIAS RECIBIDO2", field: "DIAS_RECIBIDO2", width: 220 },
  { headerName: "SINIESTRO", field: "SINIESTRO", width: 180 },
  { headerName: "NOMBRE COMERCIAL TALLER", field: "NOMBRE_COMERCIAL_TALLER", width: 250 },
  { headerName: "CIUDAD TALLER", field: "CIUDAD_TALLER", width: 150 },
  { headerName: "ESTADO TALLER", field: "ESTADO_TALLER", width: 150 },
  { headerName: "NOMBRE CONTACTO", field: "NOMBRE_CONTACTO", width: 180 },
  { headerName: "TELEFONO", field: "TELEFONO", width: 130 },
  { headerName: "EMAIL", field: "EMAIL", width: 200 },
  { headerName: "ARMADORA", field: "ARMADORA", width: 120 },
  { headerName: "MODELO", field: "MODELO", width: 180 },
  { headerName: "ANIO", field: "ANIO", width: 80 },
  { headerName: "RFC PROVEEDOR", field: "RFC_PROVEEDOR", width: 150 },
  { headerName: "RAZON SOCIAL PROVEEDOR", field: "RAZON_SOCIAL_PROVEEDOR", width: 200 },
  { headerName: "NOMBRE COMERCIAL PROVEEDOR", field: "NOMBRE_COMERCIAL_PROVEEDOR", width: 200 },
  { headerName: "COLUMNA1", field: "COLUMNA1", width: 120 },
  { headerName: "ESTADO PROVEEDOR", field: "ESTADO_PROVEEDOR", width: 150 },
  { headerName: "ITEM", field: "ITEM", width: 230 },
  { headerName: "ORIGEN", field: "ORIGEN", width: 100 },
  { headerName: "PRECIO", field: "PRECIO", width: 90 },
  { headerName: "ESTATUS", field: "ESTATUS", width: 120 },
  { headerName: "BACK ORDER", field: "BACK_ORDER", width: 120 },
  {
    headerName: "CODIGO",
    field: "CODIGO",
    width: 150,
    editable: true
  },
  { headerName: "COSTO", field: "COSTO", width: 100, editable: true },
  { headerName: "LOCALIDAD", field: "LOCALIDAD", width: 150 },
  { headerName: "CHOFER", field: "CHOFER", width: 120, editable: true },
  { headerName: "COMPAQ", field: "COMPAQ", width: 120 },
  { headerName: "OC", field: "OC", width: 100 },
  { headerName: "NUEVO ESTATUS", field: "NUEVO_ESTATUS", width: 150 },
  { headerName: "ESTATUS LOCAL", field: "ESTATUS_LOCAL", width: 150 },
  { headerName: "ESTATUS FORANEO", field: "ESTATUS_FORANEO", width: 150 },
  { headerName: "ESTATUS OPERATIVO", field: "ESTATUS2", width: 180, minWidth: 170 },
  { headerName: "Tipo de siniestro", field: "Tipo de siniestro", width: 180 },
  { headerName: "VIN", field: "VIN", width: 180 },
  { headerName: "Placa de matrícula", field: "Placa de matrícula", width: 180 },
  { headerName: "Municipio y colonia del taller", field: "Municipio y colonia del taller", width: 230 },
  { headerName: "Tipo de Valuación", field: "Tipo de Valuación", width: 180 },
  { headerName: "Proceso de creación", field: "Proceso de creación", width: 180 },
  { headerName: "Asignado automáticamente", field: "Asignado automáticamente", width: 210 },
  { headerName: "Fecha de facturación X", field: "Fecha de facturación X", width: 190, valueFormatter: formatFecha },
  { headerName: "Fecha de pago X", field: "Fecha de pago X", width: 170, valueFormatter: formatFecha },
  { headerName: "Días de cotización", field: "Días de cotización", width: 160 },
  { headerName: "ID de proveedor", field: "ID de proveedor", width: 150 },
  { headerName: "Calificación del proveedor", field: "Calificación del proveedor", width: 200 },
  { headerName: "Compañía de envío", field: "Compañía de envío", width: 180 },
  { headerName: "Nombre del almacén", field: "Nombre del almacén", width: 180 },
  { headerName: "Número de pieza solicitada", field: "Número de pieza solicitada", width: 220 },
  { headerName: "ID de pieza de la cotización", field: "ID de pieza de la cotización", width: 230 },
  { headerName: "Pieza del PIES", field: "Pieza del PIES", width: 180 },
  { headerName: "Nombre de pieza en inventario", field: "Nombre de pieza en inventario", width: 240 },
  { headerName: "Marca de la pieza", field: "Marca de la pieza", width: 170 },
  { headerName: "Notas de la pieza", field: "Notas de la pieza", width: 220 },
  { headerName: "Datos administrativos", field: "Datos administrativos", width: 220 },
  { headerName: "Vehículo en el piso", field: "Vehículo en el piso", width: 170 },
  { headerName: "Cotizado automáticamente", field: "Cotizado automáticamente", width: 210 },
  { headerName: "Cantidad", field: "Cantidad", width: 120 },
  { headerName: "Precio antiguo", field: "Precio antiguo", width: 150 },
  { headerName: "Gastos de envío", field: "Gastos de envío", width: 160 },
  { headerName: "Gastos de devolución", field: "Gastos de devolución", width: 180 },
  { headerName: "Precio total X", field: "Precio total X", width: 160 },
];

const ORDENES_COLUMN_DEFS = [
  { headerName: '', field: 'checked', checkboxSelection: true, headerCheckboxSelection: true, width: 40, pinned: 'left' },
  { headerName: 'PEDIDO', field: 'PEDIDO', flex: 1, minWidth: 160 },
  { headerName: 'ORDEN PROVEEDOR', field: 'ORDEN_PROVEEDOR', flex: 1.2, minWidth: 220 }
];

const CODIGO_NOMBRE_KEYS = [
  'nombredelproducto',
  'nombreproducto',
  'nombre',
  'producto',
  'descripcionproducto',
  'descripcion'
];

const CODIGO_VALOR_KEYS = [
  'codigodelproducto',
  'codigoproducto',
  'codigo',
  'sku',
  'clave',
  'identificador',
  'idproducto'
];

const normalizeHeaderKey = (key) => (
  key ? key.toString().toLowerCase().replace(/[^a-z0-9]/g, '') : ''
);

const normalizeCellValue = (value) => {
  if (value === null || typeof value === 'undefined') return '';
  return String(value).trim();
};


const normalizeQualitasCityText = (value) => (
  (value ?? '')
    .toString()
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
);

const QUALITAS_XNUUP_LOCAL_CITIES = [
  'GUADALAJARA',
  'ZAPOPAN',
  'TONALA',
  'TLAQUEPAQUE',
  'TLAJOMULCO'
];

const isQualitasXnuupRow = (row) => {
  const aseguradora = normalizeQualitasCityText(row?.ASEGURADORA);

  return (
    aseguradora.includes('MULTIASISTENCIA')
    || aseguradora.includes('QUALITAS')
    || aseguradora.includes('XNUUP')
    || aseguradora.includes('XNUUB')
  );
};

const resolveQualitasXnuupLocalidadByCiudad = (row) => {
  if (!isQualitasXnuupRow(row)) return '';

  const ciudad = normalizeQualitasCityText(row?.CIUDAD_TALLER ?? row?.['CIUDAD TALLER'] ?? '');
  if (!ciudad) return '';

  const esLocal = QUALITAS_XNUUP_LOCAL_CITIES.some((localCity) => (
    ciudad === localCity || ciudad.includes(localCity)
  ));

  return esLocal ? 'local' : 'foraneo';
};


const normalizeLocalidadValue = (value) => (
  (value ?? '').toString().trim().toLowerCase()
);

const buildLocalidadEchoKey = (id, value) => (
  `${String(id ?? '')}|||${String(value ?? '')}`
);

const extractFieldValue = (row, candidates) => {
  if (!row || typeof row !== 'object') return '';
  for (const [key, value] of Object.entries(row)) {
    const normalized = normalizeHeaderKey(key);
    if (candidates.some(candidate => normalized.includes(candidate))) {
      return normalizeCellValue(value);
    }
  }
  return '';
};

const compareCodigosDesc = (a, b) => {
  const nombreCompare = b.nombre.localeCompare(a.nombre, 'es', { sensitivity: 'base' });
  if (nombreCompare !== 0) return nombreCompare;
  return b.codigo.localeCompare(a.codigo, 'es', { sensitivity: 'base' });
};

const sortCodigosDesc = (rows) => {
  return [...rows].sort(compareCodigosDesc);
};

const mapCodigosRows = (rows) => {
  if (!Array.isArray(rows)) return [];
  return sortCodigosDesc(rows
    .map((row, index) => {
      const nombre = normalizeCellValue(row?.nombre ?? row?.nombre_producto ?? row?.nombreProducto ?? row?.NOMBRE ?? row?.Nombre);
      const codigo = normalizeCellValue(row?.codigo ?? row?.codigo_producto ?? row?.codigoProducto ?? row?.CODIGO ?? row?.Codigo);
      if (!nombre && !codigo) return null;
      const id = row?.id ?? row?.ID ?? `cod-${Date.now()}-${index}`;
      const nombreLower = nombre.toLowerCase();
      const codigoLower = codigo.toLowerCase();
      return {
        id,
        nombre: nombre || 'Sin nombre',
        codigo,
        searchText: `${nombre || ''} ${codigo || ''}`.toLowerCase(),
        nombreLower,
        codigoLower
      };
    })
    .filter(Boolean));
};

const useDebouncedValue = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const LOCAL_STORAGE_COLS_KEY = 'visibleColumns';
const LOCAL_STORAGE_PANEL_KEY = 'showColumnPanel';
const CODIGOS_PAGE_SIZE = 200;
const CODIGOS_SEARCH_DEBOUNCE_MS = 450;
const CODIFICAR_PANEL_HEIGHT = 360;

const CodificarPage = () => {
  const [rowData, setRowData] = useState([]);
  const [excelData, setExcelData] = useState([]);
  const [paresSiniestroItemBase, setParesSiniestroItemBase] = useState([]);
  const [paresPedidoItemBase, setParesPedidoItemBase] = useState([]);
  const [llavesValeBase, setLlavesValeBase] = useState([]);
  const [llavesQualitasBase, setLlavesQualitasBase] = useState([]);
  const [baseHistoricaRows, setBaseHistoricaRows] = useState([]);
  const fileInputRef = useRef();
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const allColumns = BASE_COLUMN_DEFS.map(col => col.field);
    const saved = localStorage.getItem(LOCAL_STORAGE_COLS_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          return Array.from(new Set([...parsed, ...allColumns]));
        }
      } catch (error) {
        console.warn('No se pudo leer visibleColumns desde localStorage:', error);
      }
    }
    return allColumns;
  });
  const [showColumnPanel, setShowColumnPanel] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PANEL_KEY);
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [selectedCount, setSelectedCount] = useState(0);
  const gridRef = useRef();
  const ordenesGridRef = useRef();
  const ordenesFileInputRef = useRef();
  const localidadEchoSuppressionRef = useRef(new Set());
  const [inspectorCell, setInspectorCell] = useState(null);
  const [gridQuickFilter, setGridQuickFilter] = useState('');

  // NUEVO: Estado para controlar si se puede cargar información
  const [puedeCargar, setPuedeCargar] = useState(false);
  const [mostrarResumen, setMostrarResumen] = useState(false);
  const undoStackRef = useRef([]);
  const isApplyingUndoRef = useRef(false);
  const [canUndo, setCanUndo] = useState(false);

  const [ordenesData, setOrdenesData] = useState([]);
  const [searchOrdenText, setSearchOrdenText] = useState('');
  const [ordenesExcelData, setOrdenesExcelData] = useState([]);
  const [ordenesPuedeCargar, setOrdenesPuedeCargar] = useState(false);
  const [ordenesSelectedCount, setOrdenesSelectedCount] = useState(0);

  const handleCellValueChanged = params => {
    const { data, newValue, colDef, node, api } = params;
    const field = colDef.field;
    if (!field) return;
    const normalizeValue = (raw) => {
      if (raw === null || typeof raw === 'undefined') return '';
      return typeof raw === 'string' ? raw : String(raw);
    };

    const normalizeCostoAutocompletar = (raw) => {
      const original = normalizeValue(raw);
      const limpio = original
        .trim()
        .toUpperCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

      if (limpio === 'DAR') return 'DAR';
      if (limpio === 'GOVI') return 'GOVI';

      if (
        limpio === 'SU' ||
        limpio === 'SUS' ||
        limpio === 'SUSP' ||
        limpio.startsWith('SUSPENS')
      ) {
        return 'SUSPENSION';
      }

      return original;
    };

    const previousValue = normalizeValue(params.oldValue);
    const originalNewValue = normalizeValue(newValue);
    const appliedNewValue = field === "COSTO"
      ? normalizeCostoAutocompletar(originalNewValue)
      : originalNewValue;

    const nuevoCompaq =
      field === "CODIGO" && appliedNewValue && appliedNewValue.trim() !== "" ? "GENERAR" : "";

    if (!isApplyingUndoRef.current && field && data?.id != null && previousValue !== appliedNewValue) {
      undoStackRef.current.push({
        id: data.id,
        field,
        previousValue,
        newValue: appliedNewValue
      });
      while (undoStackRef.current.length > 25) {
        undoStackRef.current.shift();
      }
      setCanUndo(true);
    }

    setRowData(prev =>
      prev.map(row =>
        row.id === data.id
          ? {
              ...row,
              [field]: appliedNewValue,
              ...(field === "CODIGO" ? { COMPAQ: nuevoCompaq } : {})
            }
          : row
      )
    );

    // Solo desplaza la tabla si la fila salió de la vista; evita brincos al guardar.
  const firstDisplayedIndex = api.getFirstDisplayedRowIndex?.();
  const lastDisplayedIndex = api.getLastDisplayedRowIndex?.();
    if (
      firstDisplayedIndex != null &&
      lastDisplayedIndex != null &&
      (node.rowIndex < firstDisplayedIndex || node.rowIndex > lastDisplayedIndex)
    ) {
      api.ensureIndexVisible(node.rowIndex, 'middle');
    }

    fetch(`${API_BASE_URL}/api/excel/actualizar-celda`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: data.id,
        field,
        value: appliedNewValue
      })
    });

    if (field === "CODIGO") {
      fetch(`${API_BASE_URL}/api/excel/actualizar-celda`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: data.id,
          field: "COMPAQ",
          value: nuevoCompaq
        })
      });
    }

    if (isApplyingUndoRef.current) {
      setTimeout(() => {
        isApplyingUndoRef.current = false;
      }, 0);
    }
  };

  // Estados para localidades
  const [localidades, setLocalidades] = useState([]);
  const [localidadesExcel, setLocalidadesExcel] = useState([]);
  const localidadesFileInputRef = useRef();
  const localidadesGridRef = useRef();
  const [localidadesSeleccionadas, setLocalidadesSeleccionadas] = useState([]);
  const [localidadesSearchText, setLocalidadesSearchText] = useState('');
  const [nuevaLocalidad, setNuevaLocalidad] = useState({
    taller: '',
    localidad: '',
    codigo: '',
    nombreCompaq: '',
    nomenclatura: ''
  });
  const [agregando, setAgregando] = useState(false);
  const [editando, setEditando] = useState(null);
  const [mostrarValeManual, setMostrarValeManual] = useState(false);
  const [guardandoValeManual, setGuardandoValeManual] = useState(false);

  const cargarLocalidades = useCallback(async (clearOnError = false) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/localidades`);
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.error || payload?.mensaje || 'No se pudieron cargar las localidades');
      }
      const rows = Array.isArray(payload) ? payload : [];
      setLocalidades(rows);
      return rows;
    } catch (error) {
      if (clearOnError) {
        setLocalidades([]);
      }
      console.error('Error al cargar localidades:', error);
      return [];
    }
  }, []);

  const codigosFileInputRef = useRef();
  const codigosGridRef = useRef();
  const activeCodigoEditorRef = useRef(null);
  const inlineEditorInputRef = useRef(null);
  const inlineEditorCleanupRef = useRef(null);
  const inlineSearchAbortRef = useRef(null);
  const [codigosRows, setCodigosRows] = useState([]);
  const [codigosLoading, setCodigosLoading] = useState(false);
  const [codigosFileName, setCodigosFileName] = useState('');
  const [codigosError, setCodigosError] = useState('');
  const [codigosLastUpdated, setCodigosLastUpdated] = useState(null);
  const [codigosTotal, setCodigosTotal] = useState(0);
  const [codigosFilteredTotal, setCodigosFilteredTotal] = useState(0);
  const [codigoSearchInput, setCodigoSearchInput] = useState('');
  const [codigoSearchText, setCodigoSearchText] = useState('');
  const [codigoExactMatch, setCodigoExactMatch] = useState(false);
  const [codigosSeleccionadas, setCodigosSeleccionadas] = useState([]);
  const [codigosPage, setCodigosPage] = useState(0);
  const [codigosDrawerOpen, setCodigosDrawerOpen] = useState(false);
  const [codigosDrawerRow, setCodigosDrawerRow] = useState(null);
  const [inlineCodigoQuery, setInlineCodigoQuery] = useState('');
  const [inlineCodigoResults, setInlineCodigoResults] = useState([]);
  const [inlineCodigosLoading, setInlineCodigosLoading] = useState(false);
  const [autollenandoUsados, setAutollenandoUsados] = useState(false);
  const [nuevoCodigoManual, setNuevoCodigoManual] = useState({
    codigo: '',
    nombre: ''
  });
  const debouncedCodigoSearchInput = useDebouncedValue(
    codigoSearchInput,
    CODIGOS_SEARCH_DEBOUNCE_MS
  );

  const fetchCodigos = useCallback(async ({
    page = 0,
    query = '',
    exact = false,
    skipLoading = false
  } = {}) => {
    if (!skipLoading) setCodigosLoading(true);
    setCodigosError('');

    const trimmedQuery = query.trim();
    const limit = CODIGOS_PAGE_SIZE;
    const offset = page * limit;
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    let endpoint;

    if (trimmedQuery) {
      params.set('q', trimmedQuery);
      if (exact) params.set('exact', 'true');
      endpoint = `${API_BASE_URL}/api/codigos/buscar?${params.toString()}`;
    } else {
      endpoint = `${API_BASE_URL}/api/codigos?${params.toString()}`;
    }

    try {
      const response = await fetch(endpoint);
      if (!response.ok) {
        throw new Error('No se pudo obtener el catálogo de códigos');
      }
      const payload = await response.json().catch(() => ({}));
      const registros = Array.isArray(payload?.registros)
        ? payload.registros
        : Array.isArray(payload)
          ? payload
          : [];
      const mapped = mapCodigosRows(registros);
      setCodigosRows(mapped);
      setCodigosLastUpdated(new Date());
      setCodigosSeleccionadas([]);
      codigosGridRef.current?.api?.deselectAll();

      const total = Number(payload?.total) || 0;
      if (trimmedQuery) {
        setCodigosFilteredTotal(total);
      } else {
        setCodigosTotal(total);
        setCodigosFilteredTotal(total);
      }
    } catch (error) {
      console.error('Error al cargar el catálogo de códigos:', error);
      setCodigosError(error?.message || 'No se pudo cargar el catálogo de códigos');
    } finally {
      if (!skipLoading) {
        setCodigosLoading(false);
      }
    }
  }, []);

  const syncInlineEditorValue = useCallback((value) => {
    const normalized = value ?? '';
    if (inlineEditorInputRef.current) {
      if (inlineEditorInputRef.current.value !== normalized) {
        inlineEditorInputRef.current.value = normalized;
        inlineEditorInputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
      }
    } else if (activeCodigoEditorRef.current?.setValue) {
      activeCodigoEditorRef.current.setValue(normalized);
    }
  }, []);

  const handleInlineCodigoInputChange = useCallback((value) => {
    const nextValue = value ?? '';
    setInlineCodigoQuery(nextValue);
    syncInlineEditorValue(nextValue);
  }, [syncInlineEditorValue]);

  const handleOpenCodigosDrawer = useCallback((row) => {
    setCodigosDrawerRow(row || null);
    setInlineCodigoQuery((row?.CODIGO ?? '').toString());
    setCodigosDrawerOpen(true);
  }, []);

  const handleCloseCodigosDrawer = useCallback((options = {}) => {
    const { resetQuery = true, keepResults = false } = typeof options === 'object' && options !== null ? options : {};
    inlineSearchAbortRef.current?.abort();
    inlineSearchAbortRef.current = null;
    if (!keepResults) {
      setInlineCodigoResults([]);
    }
    setInlineCodigosLoading(false);
    setCodigosDrawerOpen(false);
    setCodigosDrawerRow(null);
    if (resetQuery) {
      setInlineCodigoQuery('');
    }
  }, []);

  const handleInlineCodigoPick = useCallback((row) => {
    if (!row) return;
    const codigoSeleccionado = row.codigo || '';
    handleInlineCodigoInputChange(codigoSeleccionado);
    setInlineCodigoQuery(codigoSeleccionado);

    const rowId = codigosDrawerRow?.id;
    if (rowId != null && gridRef.current?.api) {
      const rowNode = gridRef.current.api.getRowNode(String(rowId));
      rowNode?.setDataValue('CODIGO', codigoSeleccionado);
    }

    setTimeout(() => {
      gridRef.current?.api?.stopEditing();
      setTimeout(() => {
        handleCloseCodigosDrawer({ resetQuery: false, keepResults: true });
      }, 0);
    }, 0);
  }, [handleInlineCodigoInputChange, handleCloseCodigosDrawer, codigosDrawerRow]);

  const usuario = JSON.parse(localStorage.getItem('usuario') || '{}');
  const esSupervisor = hasUserRole(usuario, 'supervisor');
  const esCaptura = hasUserRole(usuario, 'captura');
  const puedeEditarPedido = esSupervisor || esCaptura;
  const canUseOrdenesTab = true;
  const canUseLocalidadesTab = true;
  const canUseCodigosTab = true;

  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem('activeTab');
    if (saved === 'principal') return 'principal';
    if (saved === 'ordenes' && canUseOrdenesTab) return 'ordenes';
    if (saved === 'localidades' && canUseLocalidadesTab) return 'localidades';
    if (saved === 'codigos' && canUseCodigosTab) return 'codigos';
    return 'principal';
  });

  const cargarOrdenesProveedor = useCallback(() => {
    fetch(`${API_BASE_URL}/api/basedatos/ordenes-proveedor/obtener`)
      .then(res => res.json())
      .then(data => {
        if (!Array.isArray(data)) {
          setOrdenesData([]);
          return;
        }
        setOrdenesData(data);
      })
      .catch(() => setOrdenesData([]));
  }, []);

  useEffect(() => {
    // Cargar datos al montar
    const cargarDatos = () => {
      fetch(`${API_BASE_URL}/api/excel/obtener-excel`)
        .then(res => res.json())
        .then(data => setRowData(Array.isArray(data) ? data : []));
    };
    cargarDatos();

    const socket = socketIOClient(SOCKET_URL);
    socket.on('excel_data_updated', cargarDatos);
    socket.on('celda_actualizada', ({ id, field, value, compaq }) => {
      const normalizedId = id == null ? '' : String(id);
      if (!normalizedId) return;

      if (field === 'LOCALIDAD') {
        const echoKey = buildLocalidadEchoKey(normalizedId, value);
        if (localidadEchoSuppressionRef.current.has(echoKey)) {
          localidadEchoSuppressionRef.current.delete(echoKey);
          return;
        }
      }

      setRowData((prev) => {
        if (!Array.isArray(prev) || !prev.length) return prev;
        const incomingValue = value == null ? '' : String(value);
        const incomingCompaq = compaq == null ? '' : String(compaq);
        let changed = false;

        const nextRows = prev.map((row) => {
          if (String(row?.id ?? '') !== normalizedId) return row;

          const currentFieldValue = row?.[field] == null ? '' : String(row[field]);
          const currentCompaqValue = row?.COMPAQ == null ? '' : String(row.COMPAQ);
          const fieldChanged = currentFieldValue !== incomingValue;
          const compaqChanged = compaq !== undefined && currentCompaqValue !== incomingCompaq;

          if (!fieldChanged && !compaqChanged) {
            return row;
          }

          changed = true;
          return {
            ...row,
            [field]: incomingValue,
            ...(compaq !== undefined ? { COMPAQ: incomingCompaq } : {})
          };
        });

        return changed ? nextRows : prev;
      });
    });

    return () => socket.disconnect();
  }, []);

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_COLS_KEY, JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_PANEL_KEY, JSON.stringify(showColumnPanel));
  }, [showColumnPanel]);

  useEffect(() => {
    const cargarClavesBase = () => {
      fetch(`${API_BASE_URL}/api/basedatos/obtener?full=1`)
        .then(res => res.json())
        .then(data => {
          if (!Array.isArray(data)) {
            setParesSiniestroItemBase([]);
            setParesPedidoItemBase([]);
            setLlavesValeBase([]);
            setLlavesQualitasBase([]);
            setBaseHistoricaRows([]);
            return;
          }

          setBaseHistoricaRows(data);

          const siniestroPairs = [];
          const pedidoPairs = [];
          const valeKeys = [];
          const qualitasKeys = [];

          data.forEach(row => {
            const sinKey = buildSiniestroItemKey(extractSiniestro(row), extractItem(row));
            if (sinKey) siniestroPairs.push(sinKey);
            const pedKey = buildPedidoItemKey(extractPedido(row), extractItem(row));
            if (pedKey) pedidoPairs.push(pedKey);
            if (isValeManualRow(row)) {
              const key = buildValeManualKey(row);
              if (key) valeKeys.push(key);
            }
            if (isQualitasRow(row)) {
              const key = buildQualitasKey(row);
              if (key) qualitasKeys.push(key);
            }
          });

          setParesSiniestroItemBase(siniestroPairs);
          setParesPedidoItemBase(pedidoPairs);
          setLlavesValeBase(valeKeys);
          setLlavesQualitasBase(qualitasKeys);
        })
        .catch(err => {
          console.error('No se pudieron cargar claves de base de datos:', err);
          setParesSiniestroItemBase([]);
          setParesPedidoItemBase([]);
            setLlavesValeBase([]);
            setLlavesQualitasBase([]);
          setBaseHistoricaRows([]);
        });
    };

    cargarClavesBase();
    window.addEventListener('refreshBaseDatos', cargarClavesBase);
    return () => window.removeEventListener('refreshBaseDatos', cargarClavesBase);
  }, []); // SOLO una vez al montar

  useEffect(() => {
    cargarLocalidades(true);
  }, [cargarLocalidades]);

  useEffect(() => {
    localStorage.setItem('activeTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'principal') {
      setInspectorCell(null);
    }
  }, [activeTab]);

  useEffect(() => {
    if (!canUseOrdenesTab) return;
    cargarOrdenesProveedor();
    window.addEventListener('refreshOrdenesProveedor', cargarOrdenesProveedor);
    return () => window.removeEventListener('refreshOrdenesProveedor', cargarOrdenesProveedor);
  }, [canUseOrdenesTab, cargarOrdenesProveedor]);

  useEffect(() => {
    const query = codigoSearchText.trim();
    fetchCodigos({ page: codigosPage, query, exact: codigoExactMatch });
  }, [codigosPage, codigoSearchText, codigoExactMatch, fetchCodigos]);

  useEffect(() => {
    setCodigosPage(prev => (prev === 0 ? prev : 0));
  }, [codigoSearchText, codigoExactMatch]);

  useEffect(() => {
    const maxPage = codigosFilteredTotal > 0
      ? Math.max(0, Math.ceil(codigosFilteredTotal / CODIGOS_PAGE_SIZE) - 1)
      : 0;
    setCodigosPage(prev => (prev > maxPage ? maxPage : prev));
  }, [codigosFilteredTotal]);

  useEffect(() => {
    setCodigoSearchText(prev => (
      prev === debouncedCodigoSearchInput ? prev : debouncedCodigoSearchInput
    ));
  }, [debouncedCodigoSearchInput]);

  const columnDefs = useMemo(() =>
    BASE_COLUMN_DEFS.map(column => {
      if (column.field === 'PEDIDO') {
        return { ...column, editable: params => puedeEditarPedido && (isMultiasistenciaRow(params?.data) || isQualitasRow(params?.data)) };
      }
        if (column.field === 'NUEVO_ESTATUS') {
          return { ...column, editable: params => isMultiasistenciaRow(params?.data) || isValeManualRow(params?.data) };
        }
      return column;
    }),
  [puedeEditarPedido]);
  const codigosHasSearch = Boolean(codigoSearchText.trim());
  const codigosPageCount = codigosFilteredTotal > 0
    ? Math.ceil(codigosFilteredTotal / CODIGOS_PAGE_SIZE)
    : 1;
  const codigosShowingFrom = codigosFilteredTotal === 0 ? 0 : codigosPage * CODIGOS_PAGE_SIZE + 1;
  const codigosShowingTo = codigosFilteredTotal === 0
    ? 0
    : Math.min(codigosFilteredTotal, (codigosPage + 1) * CODIGOS_PAGE_SIZE);
  const codigosCanGoPrev = codigosPage > 0;
  const codigosCanGoNext = (codigosPage + 1) * CODIGOS_PAGE_SIZE < codigosFilteredTotal;

  const filteredOrdenes = useMemo(() => {
    if (!searchOrdenText) return ordenesData;
    const lower = searchOrdenText.toLowerCase();
    return ordenesData.filter((row) =>
      [row?.PEDIDO, row?.ORDEN_PROVEEDOR]
        .map((value) => (value == null ? '' : String(value).toLowerCase()))
        .some((value) => value.includes(lower))
    );
  }, [ordenesData, searchOrdenText]);

  // CRUCE AUTOMÁTICO DE LOCALIDADES
  useEffect(() => {
    if (!Array.isArray(localidades) || localidades.length === 0 || !Array.isArray(rowData) || rowData.length === 0) return;
  
    const diccionarioLocalidades = {};
      localidades.forEach(loc => {
      const key = (loc.taller || loc.Taller || '').trim().toUpperCase();
        if (!key || typeof diccionarioLocalidades[key] !== 'undefined') return;
        diccionarioLocalidades[key] = loc.localidad || loc.Localidad || loc['NOMENCLATURA'] || '';
    });

    const cambiosPendientesById = new Map();
  
    const nuevoRowData = rowData.map(row => {
      const nombreTaller = (row.NOMBRE_COMERCIAL_TALLER || row['NOMBRE COMERCIAL TALLER'] || '').trim().toUpperCase();
      const localidadSugerida = (resolveQualitasXnuupLocalidadByCiudad(row) || diccionarioLocalidades[nombreTaller] || '').toString().trim();
      const localidadActual = (row?.LOCALIDAD ?? '').toString().trim();
      if (!localidadSugerida) return row;

      // Evita sobrescribir valores existentes y solo completa filas vacías.
      if (localidadActual !== '') return row;

      if (normalizeLocalidadValue(localidadActual) !== normalizeLocalidadValue(localidadSugerida)) {
        if (row?.id != null) {
          const key = String(row.id);
          if (!cambiosPendientesById.has(key)) {
            cambiosPendientesById.set(key, { id: row.id, localidad: localidadSugerida });
          }
        }
        return { ...row, LOCALIDAD: localidadSugerida };
      }

      return row;
    });

    const cambiosPendientes = Array.from(cambiosPendientesById.values());
  
    const hayCambios = nuevoRowData.some((row, idx) => (
      normalizeLocalidadValue(row?.LOCALIDAD) !== normalizeLocalidadValue(rowData[idx]?.LOCALIDAD)
    ));

    if (hayCambios) {
      setRowData(nuevoRowData);

      if (!cambiosPendientes.length) return;

      // Persistir solo los cambios de LOCALIDAD evita reinsertar filas borradas por guardados masivos.
      Promise.allSettled(
        cambiosPendientes.map((cambio) => {
          const echoKey = buildLocalidadEchoKey(cambio.id, cambio.localidad);
          localidadEchoSuppressionRef.current.add(echoKey);

          // Limpia la llave aunque no llegue el eco de socket por reconexión/desfase.
          window.setTimeout(() => {
            localidadEchoSuppressionRef.current.delete(echoKey);
          }, 8000);

          return fetch(`${API_BASE_URL}/api/excel/actualizar-celda`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: cambio.id, field: 'LOCALIDAD', value: cambio.localidad })
          }).then((response) => {
            if (!response.ok) {
              localidadEchoSuppressionRef.current.delete(echoKey);
              throw new Error(`Error ${response.status} al sincronizar localidad`);
            }
            return response;
          }).catch((error) => {
            localidadEchoSuppressionRef.current.delete(echoKey);
            throw error;
          });
        })
      ).then((results) => {
        const hasFailure = results.some((result) => result.status === 'rejected');
        if (hasFailure) {
          console.error('Algunas localidades no se pudieron sincronizar automáticamente.');
        }
      }).catch((error) => {
        console.error('No se pudo guardar el cruce automático de localidades:', error);
      });
    }
  }, [localidades, rowData]);

  const onSelectionChanged = () => {
    if (gridRef.current) {
      const selectedRows = gridRef.current.api.getSelectedRows();
      setSelectedCount(selectedRows.length);
    }
  };

  const onOrdenSelectionChanged = () => {
    if (ordenesGridRef.current) {
      const selectedRows = ordenesGridRef.current.api.getSelectedRows();
      setOrdenesSelectedCount(selectedRows.length);
    }
  };

  const handleDeleteOrdenes = async () => {
    if (!esSupervisor) return;
    if (!ordenesGridRef.current) return;
    const selectedRows = ordenesGridRef.current.api.getSelectedRows();
    if (selectedRows.length === 0) {
      alert('Selecciona al menos un renglón para borrar.');
      return;
    }
    const ids = selectedRows
      .map((row) => Number(row?.id))
      .filter((value) => Number.isInteger(value));
    if (!ids.length) {
      alert('No se pudieron identificar los registros seleccionados.');
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/basedatos/ordenes-proveedor/borrar`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids })
      });
      const data = await res.json();
      if (data.ok) {
        cargarOrdenesProveedor();
        setOrdenesSelectedCount(0);
        ordenesGridRef.current.api.deselectAll();
      } else {
        alert(data.mensaje || 'Error al borrar');
      }
    } catch (err) {
      alert('Error de conexión al borrar');
    }
  };

  const handleOrdenesFileUpload = (event) => {
    const file = event.target?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const binary = evt.target?.result;
      if (!binary) return;
      const workbook = XLSX.read(binary, { type: 'binary' });
      const sheetName = workbook.SheetNames[0];
      if (!sheetName) return;
      const worksheet = workbook.Sheets[sheetName];
      const parsed = XLSX.utils.sheet_to_json(worksheet, { defval: '' })
        .map((row) => ({
          PEDIDO: row.PEDIDO || row.pedido || row.Pedido || row['PEDIDO '] || '',
          ORDEN_PROVEEDOR: row['ORDEN PROVEEDOR'] || row.ORDEN_PROVEEDOR || row.orden_proveedor || row['Orden proveedor'] || ''
        }))
        .filter((row) => row.PEDIDO || row.ORDEN_PROVEEDOR);
      setOrdenesExcelData(parsed);
      setOrdenesPuedeCargar(parsed.length > 0);
      if (ordenesFileInputRef.current) {
        ordenesFileInputRef.current.value = '';
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleCargarOrdenesExcel = async () => {
    if (!ordenesExcelData.length) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/basedatos/ordenes-proveedor/insertar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ordenesExcelData)
      });
      const payload = await response.json();
      if (payload.ok) {
        setOrdenesExcelData([]);
        setOrdenesPuedeCargar(false);
        cargarOrdenesProveedor();
      } else {
        alert(payload.mensaje || 'Error al cargar información');
      }
    } catch (err) {
      alert('Error de conexión al cargar información');
    }
  };

  // Modifica esta función:
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: 'binary' });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const data = XLSX.utils.sheet_to_json(ws, { defval: '' });
      setExcelData(data);
      setPuedeCargar(true);

      // GUARDADO AUTOMÁTICO AL SUBIR ARCHIVO
  fetch(`${API_BASE_URL}/api/excel/guardar-excel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }).then(() => {
        setPuedeCargar(false); // Deshabilita el botón porque ya se guardó
        setExcelData([]);
        // Opcional: recarga la tabla principal
  fetch(`${API_BASE_URL}/api/excel/obtener-excel`)
          .then(res => res.json())
          .then(data => setRowData(Array.isArray(data) ? data : []));
      });
    };
    reader.readAsBinaryString(file);
  };

  // Modifica esta función:
  const handleLoadData = () => {
    setRowData(prev => {
      const newData = [...prev, ...excelData];
      // Guarda automáticamente después de subir
  fetch(`${API_BASE_URL}/api/excel/guardar-excel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newData),
      });
      return newData;
    });
    setPuedeCargar(false); // Deshabilita el botón después de cargar
    setExcelData([]); // Limpia los datos cargados
  };

  const handleColumnToggle = (field) => {
    setVisibleColumns(prev =>
      prev.includes(field)
        ? prev.filter(f => f !== field)
        : [...prev, field]
    );
  };

  const filteredColumnDefs = columnDefs.filter(
    col => col.field === 'checked' || visibleColumns.includes(col.field)
  );

  const handleSave = async () => {
    try {
  await fetch(`${API_BASE_URL}/api/excel/guardar-excel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(rowData),
      });
      alert('¡Información guardada en el backend!');
    } catch (error) {
      alert('Error al guardar en el backend');
    }
  };

  const handleGuardarValeManual = async (form, localidad) => {
    try {
      setGuardandoValeManual(true);
      const renglon = buildValeManualRow(form, localidad, formatDateInputToExcelString);
      await guardarValeManual({ apiBaseUrl: API_BASE_URL, renglon, setRowData });
      setMostrarValeManual(false);
      alert('Vale manual enviado a Codificar.');
      return true;
    } catch (error) {
      alert(error.message || 'Error al guardar vale manual');
      return false;
    } finally {
      setGuardandoValeManual(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (!esSupervisor) return;
    if (!gridRef.current) return;
    const selectedRows = gridRef.current.api.getSelectedRows();
    if (selectedRows.length === 0) {
      alert('Selecciona al menos un renglón para borrar.');
      return;
    }
    const ids = selectedRows.map(row => row.id);
    try {
  const res = await fetch(`${API_BASE_URL}/api/excel/borrar`, {
        method: 'DELETE',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids })
      });
      const data = await res.json();
      if (data.ok) {
  fetch(`${API_BASE_URL}/api/excel/obtener-excel`)
          .then(res => res.json())
          .then(data => setRowData(Array.isArray(data) ? data : []));
        setSelectedCount(0);
        gridRef.current.api.deselectAll();
      } else {
        alert(data.mensaje || 'Error al borrar');
      }
    } catch (err) {
      alert('Error de conexión al borrar');
    }
  };

  const siniestroItemBaseSet = useMemo(() => {
    const source = Array.isArray(paresSiniestroItemBase) ? paresSiniestroItemBase : [];
    return new Set(source.filter(Boolean));
  }, [paresSiniestroItemBase]);

  const pedidoItemBaseSet = useMemo(() => {
    const source = Array.isArray(paresPedidoItemBase) ? paresPedidoItemBase : [];
    return new Set(source.filter(Boolean));
  }, [paresPedidoItemBase]);
  const valeBaseSet = useMemo(() => new Set(llavesValeBase.filter(Boolean)), [llavesValeBase]);
  const qualitasBaseSet = useMemo(() => new Set(llavesQualitasBase.filter(Boolean)), [llavesQualitasBase]);

  const siniestroItemDuplicadosCodificar = useMemo(() => {
    if (!Array.isArray(rowData) || rowData.length === 0) {
      return new Set();
    }
    const counts = new Map();
    rowData.forEach(row => {
      const key = buildSiniestroItemKey(extractSiniestro(row), extractItem(row));
      if (!key) return;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    const duplicados = new Set();
    counts.forEach((count, key) => {
      if (count > 1) duplicados.add(key);
    });
    return duplicados;
  }, [rowData]);

  const pedidoItemDuplicadosCodificar = useMemo(() => {
    if (!Array.isArray(rowData) || rowData.length === 0) {
      return new Set();
    }
    const counts = new Map();
    rowData.forEach(row => {
      const key = buildPedidoItemKey(extractPedido(row), extractItem(row));
      if (!key) return;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    const duplicados = new Set();
    counts.forEach((count, key) => {
      if (count > 1) duplicados.add(key);
    });
    return duplicados;
  }, [rowData]);

  const valeDuplicadosCodificar = useMemo(() => {
    const c = new Map();
    rowData.filter(isValeManualRow).forEach(r => {
      const k = buildValeManualKey(r);
      if (k) c.set(k, (c.get(k) || 0) + 1);
    });
    return new Set([...c].filter(([, n]) => n > 1).map(([k]) => k));
  }, [rowData]);

  const qualitasDuplicadosCodificar = useMemo(() => {
    const c = new Map();
    rowData.filter(isQualitasRow).forEach(r => {
      const k = buildQualitasKey(r);
      if (k) c.set(k, (c.get(k) || 0) + 1);
    });
    return new Set([...c].filter(([, n]) => n > 1).map(([k]) => k));
  }, [rowData]);

  const inspectorRowData = useMemo(() => {
    if (!inspectorCell) return null;
    const { rowId, rowKey, snapshot } = inspectorCell;
    if (rowId != null) {
      const matchById = rowData.find(row => row && row.id === rowId);
      if (matchById) return matchById;
    }
    if (rowKey) {
      const matchByKey = rowData.find(row => buildPedidoItemKey(extractPedido(row), extractItem(row)) === rowKey);
      if (matchByKey) return matchByKey;
    }
    return snapshot || null;
  }, [inspectorCell, rowData]);

  const inspectorField = inspectorCell?.field ?? null;
  const inspectorHeader = inspectorCell?.header ?? 'Inspector de celdas';
  const inspectorColumn = inspectorField ? columnDefs.find(col => col.field === inspectorField) : null;
  const inspectorIsEditable = Boolean(inspectorColumn?.editable);
  const inspectorPedidoLabel = inspectorRowData && inspectorRowData.PEDIDO != null
    ? String(inspectorRowData.PEDIDO).trim()
    : '';
  const inspectorSiniestroLabel = inspectorRowData && inspectorRowData.SINIESTRO != null
    ? String(inspectorRowData.SINIESTRO).trim()
    : '';
  const inspectorModeloAnioLabel = [inspectorRowData?.MODELO, inspectorRowData?.ANIO]
    .map(val => (val == null ? '' : String(val).trim()))
    .filter(Boolean)
    .join(' ');
  const inspectorTallerLabel = inspectorRowData && inspectorRowData.NOMBRE_COMERCIAL_TALLER != null
    ? String(inspectorRowData.NOMBRE_COMERCIAL_TALLER).trim()
    : '';
  const inspectorOrigenLabel = inspectorRowData && inspectorRowData.ORIGEN != null
    ? String(inspectorRowData.ORIGEN).trim()
    : '';
  const inspectorValueRaw = inspectorField
    ? (inspectorRowData?.[inspectorField] ?? inspectorCell?.snapshot?.[inspectorField] ?? '')
    : '';
  const inspectorValueDisplay = inspectorValueRaw == null ? '' : String(inspectorValueRaw);
  const inspectorInstruction = inspectorCell
    ? (inspectorIsEditable
      ? 'Edita esta columna directamente en la tabla.'
      : 'Solo lectura.')
    : 'Selecciona una celda para ver su contenido.';

  const updateInspectorFromData = useCallback((field, header, data) => {
    if (!field || field === 'checked' || !data) {
      setInspectorCell(null);
      return;
    }
    const resolvedHeader = header || field;
    const rowId = data.id ?? null;
    const rowKey = buildPedidoItemKey(extractPedido(data), extractItem(data));
    setInspectorCell({
      field,
      header: resolvedHeader,
      rowId,
      rowKey,
      snapshot: data
    });
  }, [setInspectorCell]);

  const handleCellClicked = useCallback((params) => {
    const field = params?.colDef?.field;
    const header = params?.colDef?.headerName;
    const data = params?.data || null;
    updateInspectorFromData(field, header, data);
  }, [updateInspectorFromData]);

  const handleCellFocused = useCallback((params) => {
    const column = params?.column;
    if (!column) {
      setInspectorCell(null);
      return;
    }
    const colDef = column.getColDef?.() || {};
    const field = colDef.field;
    if (!field || field === 'checked') {
      setInspectorCell(null);
      return;
    }
    const rowIndex = params?.rowIndex;
    if (typeof rowIndex !== 'number' || rowIndex < 0) {
      setInspectorCell(null);
      return;
    }
    const node = params.api?.getDisplayedRowAtIndex(rowIndex);
    const data = node?.data || null;
    updateInspectorFromData(field, colDef.headerName, data);
  }, [updateInspectorFromData, setInspectorCell]);

  const handleInspectorClear = useCallback(() => {
    setInspectorCell(null);
    const api = gridRef.current?.api;
    if (api) {
      api.clearFocusedCell();
      api.deselectAll();
    }
  }, [setInspectorCell]);

  const handleGridQuickFilterChange = useCallback((value) => {
    const nextValue = value ?? '';
    setGridQuickFilter(nextValue);
  }, []);


  const handleAutollenarUsadosOriginales = async () => {
    if (autollenandoUsados) return;

    if (!Array.isArray(rowData) || rowData.length === 0) {
      alert('No hay filas cargadas en Codificar.');
      return;
    }

    if (!Array.isArray(baseHistoricaRows) || baseHistoricaRows.length === 0) {
      alert('Aún no está cargado el histórico de base de datos. Espera unos segundos y vuelve a intentar.');
      return;
    }

    let cambios = [];
    setAutollenandoUsados(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/codigos/autollenar-usados`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filas: rowData,
          historico: baseHistoricaRows
        })
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok || payload?.ok === false) {
        throw new Error(payload?.error || 'No se pudo ejecutar el autollenado inteligente.');
      }

      cambios = Array.isArray(payload?.cambios) ? payload.cambios : [];
    } catch (error) {
      console.error('Error al autollenar usados/originales:', error);
      alert(`No se pudo autollenar: ${error.message}`);
      setAutollenandoUsados(false);
      return;
    }

    if (cambios.length === 0) {
      alert('No encontré códigos seguros en catálogo ni histórico para llenar.');
      setAutollenandoUsados(false);
      return;
    }

    setRowData(prev => prev.map(row => {
      const cambio = cambios.find(item => String(item.id) === String(row.id));
      if (!cambio) return row;
      return {
        ...row,
        CODIGO: cambio.codigo,
        COMPAQ: 'GENERAR',
        __AUTOLLENADO_USO: true
      };
    }));

    for (const cambio of cambios) {
      await fetch(`${API_BASE_URL}/api/excel/actualizar-celda`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: cambio.id, field: 'CODIGO', value: cambio.codigo })
      });

      await fetch(`${API_BASE_URL}/api/excel/actualizar-celda`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: cambio.id, field: 'COMPAQ', value: 'GENERAR' })
      });
    }

    setAutollenandoUsados(false);
    alert(`Autollenado terminado. Se llenaron ${cambios.length} fila(s). Revisa los renglones verde fuerte. El envío a base de datos sigue siendo manual.`);
  };

  const handleEnviarSeleccionados = async () => {
    if (!gridRef.current) return;
    const selectedRows = gridRef.current.api.getSelectedRows();
    if (selectedRows.length === 0) {
      alert('Selecciona al menos un renglón para enviar.');
      return;
    }
    const duplicadosSiniestroBase = [];
    const bloqueadosPedido = [];
    const bloqueadosVale = [];
    const advertidosMulti = [];
    const advertidosQualitas = [];
    const mensajesPiezaRepetida = [];
    const piezasRepetidasVistas = new Set();
    const filasAEnviar = [];

    selectedRows.forEach(row => {
      const sinKey = buildSiniestroItemKey(extractSiniestro(row), extractItem(row));
      const pedKey = buildPedidoItemKey(extractPedido(row), extractItem(row));
      const valeKey = isValeManualRow(row) ? buildValeManualKey(row) : '';
      const qualitasKey = isQualitasRow(row) ? buildQualitasKey(row) : '';
      const usaLlaveEspecial = Boolean(valeKey || qualitasKey);
      const existeEnBasePorVale = Boolean(valeKey) && valeBaseSet.has(valeKey);
      const existeEnBasePorQualitas = Boolean(qualitasKey) && qualitasBaseSet.has(qualitasKey);
      const repetidaEnArchivoPorVale = Boolean(valeKey) && valeDuplicadosCodificar.has(valeKey);
      const repetidaEnArchivoPorQualitas = Boolean(qualitasKey) && qualitasDuplicadosCodificar.has(qualitasKey);
      const esMulti = isMultiDuplicadoRow(row);
      const existeEnBasePorSiniestro = Boolean(sinKey) && siniestroItemBaseSet.has(sinKey);
      const existeEnBasePorPedido = esMulti
        ? baseHistoricaRows.some(candidate => hasMultiPedidoConflict(row, candidate))
        : Boolean(pedKey) && pedidoItemBaseSet.has(pedKey);
      const repetidaEnArchivoPorSiniestro = Boolean(sinKey) && siniestroItemDuplicadosCodificar.has(sinKey);
      const repetidaEnArchivoPorPedido = esMulti
        ? rowData.some(candidate =>
            !isSameCodificarRow(row, candidate)
            && hasMultiPedidoConflict(row, candidate)
          )
        : Boolean(pedKey) && pedidoItemDuplicadosCodificar.has(pedKey);

      if (!usaLlaveEspecial && !esMulti && existeEnBasePorSiniestro) {
        duplicadosSiniestroBase.push(row);
      }

      const repetidaSoloEnArchivo = usaLlaveEspecial
        ? !existeEnBasePorVale && !existeEnBasePorQualitas
          && (repetidaEnArchivoPorVale || repetidaEnArchivoPorQualitas)
        : esMulti
          ? !existeEnBasePorPedido && repetidaEnArchivoPorPedido
          : !existeEnBasePorSiniestro && !existeEnBasePorPedido
            && (repetidaEnArchivoPorSiniestro || repetidaEnArchivoPorPedido);
      if (repetidaSoloEnArchivo) {
        const siniestro = normalizeCellValue(extractSiniestro(row));
        const pieza = normalizeCellValue(extractItem(row));
        const avisoKey = valeKey || qualitasKey || `${siniestro}|||${pieza}`;
        if (!piezasRepetidasVistas.has(avisoKey)) {
          piezasRepetidasVistas.add(avisoKey);
          const aviso = valeKey
            ? `Llave repetida: SINIESTRO "${siniestro}" + ITEM "${pieza}" + OC "${extractOc(row)}"`
            : qualitasKey
              ? `Llave repetida: SINIESTRO "${siniestro}" + ITEM "${pieza}" + BACK_ORDER "${extractBackOrder(row)}"`
              : `En el pedido con siniestro "${siniestro}" se encuentra esta pieza repetida "${pieza}"`;
          mensajesPiezaRepetida.push(aviso);
        }
      }

      if (existeEnBasePorVale) {
        bloqueadosVale.push(row);
        return;
      }

      if (existeEnBasePorQualitas) {
        advertidosQualitas.push(row);
      }

      if (esMulti && existeEnBasePorPedido) {
        advertidosMulti.push(row);
      }

      if (!usaLlaveEspecial && !esMulti && existeEnBasePorPedido) {
        bloqueadosPedido.push(row);
        return;
      }

      filasAEnviar.push(row);
    });

    if (filasAEnviar.length === 0) {
      alert('Todos los seleccionados quedaron bloqueados por una llave duplicada y no se enviaron.');
      return;
    }

    // Mapea los datos exactamente con los nombres de columnas de la base de datos
    const datosAEnviar = filasAEnviar.map(completarFila);

    console.log('Enviando a base de datos:', datosAEnviar);
    try {
      const remitenteActual = (usuario?.username || usuario?.usuario || usuario?.nombreUsuario || usuario?.nombre || '').trim();
      const payload = { datos: datosAEnviar, subidoPor: remitenteActual };
	const res = await fetch(`${API_BASE_URL}/api/basedatos/insertar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      console.log('Respuesta del backend:', data);
      if (data.ok) {
  const ids = filasAEnviar.map(row => row.id);
  await fetch(`${API_BASE_URL}/api/excel/borrar`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ids }),
        });

  fetch(`${API_BASE_URL}/api/excel/obtener-excel`)
          .then(res => res.json())
          .then(data => setRowData(Array.isArray(data) ? data : []));

        window.dispatchEvent(new Event('refreshBaseDatos'));

        setParesSiniestroItemBase(prev => {
          const merged = new Set(prev);
          filasAEnviar.forEach(row => {
            const clave = buildSiniestroItemKey(extractSiniestro(row), extractItem(row));
            if (clave) {
              merged.add(clave);
            }
          });
          return Array.from(merged);
        });

        setParesPedidoItemBase(prev => {
          const merged = new Set(prev);
          filasAEnviar.forEach(row => {
            const clave = buildPedidoItemKey(extractPedido(row), extractItem(row));
            if (clave) {
              merged.add(clave);
            }
          });
          return Array.from(merged);
        });

        setLlavesValeBase(prev => [
          ...new Set([...prev, ...filasAEnviar.filter(isValeManualRow).map(buildValeManualKey).filter(Boolean)])
        ]);
        setLlavesQualitasBase(prev => [
          ...new Set([...prev, ...filasAEnviar.filter(isQualitasRow).map(buildQualitasKey).filter(Boolean)])
        ]);

        let mensaje = `¡Filas enviadas a base de datos y eliminadas de codificar! Total enviadas: ${filasAEnviar.length}.`;
        if (mensajesPiezaRepetida.length > 0) {
          mensaje += `\n\n` + mensajesPiezaRepetida.join('\n');
        }
        if (duplicadosSiniestroBase.length > 0) {
          mensaje += `\n\nAdvertencia: ${duplicadosSiniestroBase.length} fila(s) tienen SINIESTRO + ITEM duplicado en base de datos. Se enviaron de todas formas:` +
            `\n` + duplicadosSiniestroBase.map(row => `SINIESTRO: ${row.SINIESTRO || ''} | ITEM: ${row.ITEM || ''}`).join('\n');
        }
        if (advertidosMulti.length > 0) {
          mensaje += `\n\nAdvertencia: ${advertidosMulti.length} fila(s) de MULTIASISTENCIA están duplicadas según COTIZACION + ITEM y PEDIDO. Se enviaron de todas formas:` +
            `\n` + advertidosMulti.map(row => `COTIZACION: ${String(row.COTIZACION ?? "")} | PEDIDO: ${String(row.PEDIDO ?? "")} | ITEM: ${String(row.ITEM ?? "")}`).join("\n");
        }
        if (bloqueadosPedido.length > 0) {
          mensaje += `\n\nNo se enviaron ${bloqueadosPedido.length} fila(s) por PEDIDO + ITEM duplicado:` +
            `\n` + bloqueadosPedido.map(row => `PEDIDO: ${row.PEDIDO || ''} | ITEM: ${row.ITEM || ''}`).join('\n');
        }
        if (bloqueadosVale.length) {
          mensaje += `\n\nNo se enviaron ${bloqueadosVale.length} fila(s): llave VALE MANUAL duplicada (SINIESTRO + ITEM + OC).`;
        }
        if (advertidosQualitas.length) {
          mensaje += `\n\nAdvertencia: ${advertidosQualitas.length} fila(s) de QUALITAS/XNUUP tienen llave duplicada (SINIESTRO + ITEM + BACK_ORDER). Se enviaron de todas formas.`;
        }
        alert(mensaje);
      } else {
        alert(data.error || 'Error al insertar en base de datos');
      }
    } catch (err) {
      alert('Error de conexión al insertar en base de datos');
    }
  };

  const handleUndo = useCallback(() => {
    const stack = undoStackRef.current;
    if (!stack.length) return;

    const entry = stack.pop();
    setCanUndo(stack.length > 0);

    if (!entry || entry.id == null || !entry.field) return;

    const api = gridRef.current?.api;
    if (!api) {
      stack.push(entry);
      setCanUndo(true);
      return;
    }

    let targetNode = api.getRowNode(String(entry.id));
    if (!targetNode) {
      api.forEachNode(node => {
        if (!targetNode && String(node.data?.id) === String(entry.id)) {
          targetNode = node;
        }
      });
    }

    if (!targetNode) {
      stack.push(entry);
      setCanUndo(true);
      return;
    }

    isApplyingUndoRef.current = true;
    targetNode.setDataValue(entry.field, entry.previousValue ?? '');
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!(event.ctrlKey || event.metaKey)) return;
      if (event.key !== 'z' && event.key !== 'Z') return;
      const target = event.target;
      const tagName = (target?.tagName || '').toLowerCase();
      if (tagName === 'input' || tagName === 'textarea' || target?.isContentEditable) return;
      event.preventDefault();
      if (canUndo) {
        handleUndo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, canUndo]);

  const getRowClass = params => {
    const data = params.data || {};
    const clave = buildSiniestroItemKey(extractSiniestro(data), extractItem(data));
    const existeEnBasePorSiniestro = Boolean(clave) && siniestroItemBaseSet.has(clave);
    const repetidaEnArchivoPorSiniestro = Boolean(clave) && siniestroItemDuplicadosCodificar.has(clave);
    const pedidoClave = buildPedidoItemKey(extractPedido(data), extractItem(data));
    const esMulti = isMultiDuplicadoRow(data);
    const existeEnBasePorPedido = esMulti
      ? baseHistoricaRows.some(candidate => hasMultiPedidoConflict(data, candidate))
      : Boolean(pedidoClave) && pedidoItemBaseSet.has(pedidoClave);
    const repetidaEnArchivoPorPedido = esMulti
      ? rowData.some(candidate =>
          !isSameCodificarRow(data, candidate)
          && hasMultiPedidoConflict(data, candidate)
        )
      : Boolean(pedidoClave) && pedidoItemDuplicadosCodificar.has(pedidoClave);
    const valeClave = isValeManualRow(data) ? buildValeManualKey(data) : "";
    const qualitasClave = isQualitasRow(data) ? buildQualitasKey(data) : "";
    const usaEspecial = Boolean(valeClave || qualitasClave);
    const existeEspecial = valeBaseSet.has(valeClave) || qualitasBaseSet.has(qualitasClave);
    const repetidaEspecial = valeDuplicadosCodificar.has(valeClave) || qualitasDuplicadosCodificar.has(qualitasClave);
    const clases = [];

    if (!usaEspecial && !esMulti && existeEnBasePorSiniestro) {
      clases.push('row-texto-rojo');
    }

    if (usaEspecial ? existeEspecial : existeEnBasePorPedido) {
      clases.push('row-pedido-duplicado');
    }

    const repetidaSoloEnArchivo = usaEspecial
      ? !existeEspecial && repetidaEspecial
      : esMulti
        ? !existeEnBasePorPedido && repetidaEnArchivoPorPedido
        : !existeEnBasePorSiniestro && !existeEnBasePorPedido
          && (repetidaEnArchivoPorSiniestro || repetidaEnArchivoPorPedido);
    if (repetidaSoloEnArchivo) {
      clases.push('row-texto-azul-marino');
    }

    if (data.CODIGO && String(data.CODIGO).trim() !== "") {
      if (data.__AUTOLLENADO_USO) {
        clases.push('row-codigo-autollenado');
      } else {
        clases.push('row-codigo-lleno');
      }
    }

    return clases.join(' ');
  };

  const handleCellEditingStarted = params => {
    if (params.colDef.field === "CODIGO") {
      handleOpenCodigosDrawer(params.data);

      if (inlineEditorCleanupRef.current) {
        inlineEditorCleanupRef.current();
        inlineEditorCleanupRef.current = null;
      }

      setTimeout(() => {
        const editors = params.api?.getCellEditorInstances?.({
          rowNodes: [params.node],
          columns: [params.column]
        });
        const editorInstance = editors && editors.length ? editors[0] : null;
        activeCodigoEditorRef.current = editorInstance;
        const gui = editorInstance?.getGui?.();
        const inputEl = gui ? gui.querySelector('input, textarea, [contenteditable="true"]') : null;
        inlineEditorInputRef.current = inputEl || null;
        if (inputEl) {
          const syncFromInput = () => setInlineCodigoQuery(inputEl.value || '');
          inputEl.addEventListener('input', syncFromInput);
          inlineEditorCleanupRef.current = () => {
            inputEl.removeEventListener('input', syncFromInput);
            inlineEditorInputRef.current = null;
          };
          syncFromInput();
        } else {
          inlineEditorCleanupRef.current = null;
        }
      }, 0);

    }
  };
  const handleCellEditingStopped = params => {
    if (params?.colDef?.field === "CODIGO") {
      inlineEditorCleanupRef.current?.();
      inlineEditorCleanupRef.current = null;
      inlineEditorInputRef.current = null;
      activeCodigoEditorRef.current = null;
    }
    // NO hagas nada aquí para mover el foco
  };

  const handleLocalidadesFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: 'binary' });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      // Filtra filas vacías
      const data = XLSX.utils
        .sheet_to_json(ws, { defval: '' })
        .map(row => ({
          taller: row.taller || row.Taller || row.TALLER || '',
          localidad: row.localidad || row.Localidad || row.LOCALIDAD || row.NOMENCLATURA || '',
          codigo: row.codigo || row.CODIGO || '',
          nombreCompaq: row.nombreCompaq || row.nombre_compaq || row['NOMBRE COMPAQ'] || '',
          nomenclatura: row.nomenclatura || row.NOMENCLATURA || ''
        }))
        .filter(row => row.taller && row.localidad);
      setLocalidadesExcel(data);
    };
    reader.readAsBinaryString(file);
  };

  const handleCargarLocalidades = async () => {
    if (localidadesExcel.length === 0) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/localidades/cargar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(localidadesExcel)
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        const insertados = Number(data.insertados || 0);
        const omitidos = Number(data.omitidos || 0);
        const warning = data.warning ? String(data.warning) : '';
        if (omitidos > 0) {
          let mensaje = `Carga finalizada. Insertadas: ${insertados}. Omitidas por duplicado exacto (taller + localidad + codigo): ${omitidos}.`;
          if (warning) {
            mensaje += `\n\nAviso: ${warning}`;
          }
          alert(mensaje);
        } else {
          let mensaje = `¡Localidades cargadas correctamente! Insertadas: ${insertados}.`;
          if (warning) {
            mensaje += `\n\nAviso: ${warning}`;
          }
          alert(mensaje);
        }
        // Refresca la tabla de localidades
        await cargarLocalidades();
        setLocalidadesExcel([]);
      } else {
        alert(data.error || 'Error al cargar localidades');
      }
    } catch (err) {
      alert('Error de conexión al cargar localidades');
    }
  };

  // --- NUEVO: Agregar manualmente una localidad ---
  const handleAgregarManual = async () => {
    const taller = (nuevaLocalidad.taller || '').trim();
    const localidad = (nuevaLocalidad.localidad || '').trim();
    if (!taller || !localidad) {
      alert('Completa ambos campos');
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/localidades/cargar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify([
          {
            taller,
            localidad,
            codigo: (nuevaLocalidad.codigo || '').trim(),
            nombreCompaq: (nuevaLocalidad.nombreCompaq || '').trim(),
            nomenclatura: (nuevaLocalidad.nomenclatura || '').trim()
          }
        ])
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        const insertados = Number(data.insertados || 0);
        if (insertados === 0) {
          if (data.warning) {
            alert(`No se agregó. ${data.warning}`);
          } else {
            alert('No se agregó: ya existe una localidad con el mismo taller, localidad y código.');
          }
          return;
        }
        alert('¡Localidad agregada!');
        await cargarLocalidades();
        setNuevaLocalidad({
          taller: '',
          localidad: '',
          codigo: '',
          nombreCompaq: '',
          nomenclatura: ''
        });
        setAgregando(false);
      } else {
        alert(data.error || 'Error al agregar');
      }
    } catch {
      alert('Error de conexión');
    }
  };
  // --- FIN NUEVO ---

  const handleEditarLocalidad = (row) => {
    setEditando({ ...row });
    setAgregando(false);
  };

  const handleGuardarEdicion = async () => {
    if (!editando.taller || !editando.localidad) {
      alert('Completa ambos campos');
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/localidades/editar`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editando)
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        alert('¡Localidad editada!');
        await cargarLocalidades();
        setEditando(null);
      } else {
        alert(data.error || 'Error al editar');
      }
    } catch {
      alert('Error de conexión');
    }
  };

  const handleCancelarEdicion = () => setEditando(null);

  const handleEliminarLocalidad = async (row) => {
    if (!window.confirm('¿Seguro que deseas eliminar esta localidad?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/localidades/eliminar`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: row.id })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        alert('¡Localidad eliminada!');
        await cargarLocalidades();
      } else {
        alert(data.error || 'Error al eliminar');
      }
    } catch {
      alert('Error de conexión');
    }
  };

  const onLocalidadesSelectionChanged = () => {
    if (!localidadesGridRef.current) return;
    const rows = localidadesGridRef.current.api.getSelectedRows();
    setLocalidadesSeleccionadas(rows);
  };

  const handleEliminarLocalidadesSeleccionadas = async () => {
    if (!localidadesSeleccionadas.length) return;
    if (!window.confirm(`¿Seguro que deseas eliminar ${localidadesSeleccionadas.length} localidad(es)?`)) return;
    try {
      for (const row of localidadesSeleccionadas) {
        await fetch(`${API_BASE_URL}/api/localidades/eliminar`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: row.id })
        });
      }
      await cargarLocalidades();
      if (localidadesGridRef.current) {
        localidadesGridRef.current.api.deselectAll();
      }
      setLocalidadesSeleccionadas([]);
    } catch {
      alert('Error de conexión al eliminar localidades');
    }
  };

  const handleCodigosFileUpload = useCallback(async (event) => {
    const file = event.target?.files?.[0];
    if (!file) return;
    setCodigosLoading(true);
    setCodigosError('');
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      if (!workbook.SheetNames.length) {
        throw new Error('El archivo no contiene hojas.');
      }
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(worksheet, { defval: '', raw: false });
      if (!Array.isArray(json) || json.length === 0) {
        throw new Error('No se encontraron filas en el Excel.');
      }

      const mapped = json
        .map((row) => {
          let nombre = extractFieldValue(row, CODIGO_NOMBRE_KEYS);
          let codigo = extractFieldValue(row, CODIGO_VALOR_KEYS);
          if (!nombre || !codigo) {
            const headers = Object.keys(row);
            if (headers.length) {
              if (!nombre) nombre = normalizeCellValue(row[headers[0]]);
              if (!codigo && headers.length > 1) codigo = normalizeCellValue(row[headers[1]]);
            }
          }
          if (!nombre && !codigo) return null;
          const cleanNombre = nombre || 'Sin nombre';
          const cleanCodigo = codigo || '';
          return { nombre: cleanNombre, codigo: cleanCodigo };
        })
        .filter(Boolean);

      if (!mapped.length) {
        throw new Error('No se detectaron columnas para nombre y código.');
      }

      const response = await fetch(`${API_BASE_URL}/api/codigos/cargar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ registros: mapped })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload?.ok === false) {
        throw new Error(payload?.error || 'No se pudieron guardar los códigos');
      }

      setCodigosFileName(file.name);
      setCodigoSearchInput('');
      setCodigoSearchText('');
      setCodigosPage(0);
      await fetchCodigos({ page: 0, query: '', exact: false, skipLoading: true });
      setCodigosLastUpdated(new Date());
    } catch (error) {
      console.error('Error al procesar códigos:', error);
      setCodigosError(error?.message || 'No se pudo procesar el archivo.');
    } finally {
      setCodigosLoading(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  }, [fetchCodigos]);

  const handleAgregarCodigoManual = useCallback(async () => {
    const codigo = (nuevoCodigoManual.codigo || '').trim();
    const nombre = (nuevoCodigoManual.nombre || '').trim();

    if (!codigo || !nombre) {
      alert('Completa código y descripción del producto.');
      return;
    }

    setCodigosLoading(true);
    setCodigosError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/codigos/cargar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          registros: [
            {
              codigo,
              nombre
            }
          ]
        })
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok || payload?.ok === false) {
        throw new Error(payload?.error || 'No se pudo guardar el código.');
      }

      const insertados = Number(payload.insertados || 0);

      if (insertados === 0) {
        alert('No se agregó: el código ya existe en el catálogo.');
        return;
      }

      alert('¡Código agregado correctamente!');

      setNuevoCodigoManual({
        codigo: '',
        nombre: ''
      });

      setCodigoSearchInput('');
      setCodigoSearchText('');
      setCodigosPage(0);
      await fetchCodigos({ page: 0, query: '', exact: false, skipLoading: true });
      setCodigosLastUpdated(new Date());
    } catch (error) {
      console.error('Error al agregar código manual:', error);
      setCodigosError(error?.message || 'No se pudo guardar el código manual.');
      alert(error?.message || 'No se pudo guardar el código manual.');
    } finally {
      setCodigosLoading(false);
    }
  }, [nuevoCodigoManual, fetchCodigos]);

  const handleDownloadCodigosTemplate = useCallback(() => {
    const link = document.createElement('a');
    link.href = `${process.env.PUBLIC_URL ?? ''}/codigos_template.csv`;
    link.download = 'plantilla_codigos.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  const handleClearCodigos = useCallback(async () => {
    setCodigosError('');
    setCodigoSearchInput('');
    setCodigoSearchText('');
    setCodigosFileName('');
    setCodigosSeleccionadas([]);
    if (codigosFileInputRef.current) {
      codigosFileInputRef.current.value = '';
    }
    codigosGridRef.current?.api?.deselectAll();
    setCodigosLoading(true);
    try {
      setCodigosPage(0);
      await fetchCodigos({ page: 0, query: '', exact: false, skipLoading: true });
    } finally {
      setCodigosLoading(false);
    }
  }, [fetchCodigos]);

  const handleCodigosSelectionChanged = useCallback(() => {
    if (!codigosGridRef.current) return;
    const rows = codigosGridRef.current.api.getSelectedRows();
    setCodigosSeleccionadas(rows);
  }, []);

  const handleDeleteSelectedCodigos = useCallback(async () => {
    if (!codigosSeleccionadas.length) return;
    if (!window.confirm(`¿Deseas eliminar ${codigosSeleccionadas.length} registro(s)?`)) return;
    const ids = codigosSeleccionadas
      .map(row => Number(row.id))
      .filter(id => Number.isInteger(id) && id > 0);
    if (!ids.length) {
      setCodigosError('No se pudieron determinar los IDs a eliminar.');
      return;
    }
    setCodigosLoading(true);
    setCodigosError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/codigos/eliminar`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload?.ok === false) {
        throw new Error(payload?.error || 'No se pudieron eliminar los códigos seleccionados');
      }
      const query = codigoSearchText.trim();
      await fetchCodigos({ page: codigosPage, query, exact: codigoExactMatch, skipLoading: true });
    } catch (error) {
      console.error('Error al eliminar códigos:', error);
      setCodigosError(error?.message || 'No se pudieron eliminar los códigos seleccionados');
    } finally {
      setCodigosLoading(false);
    }
  }, [codigosSeleccionadas, codigosPage, codigoSearchText, codigoExactMatch, fetchCodigos]);

  const AccionesRenderer = (props) => (
    <div className="inline-actions">
      <button
        type="button"
        className="btn btn-inline btn-secondary"
        onClick={() => props.onEditar(props.data)}
      >
        Editar
      </button>
      <button
        type="button"
        className="btn btn-inline btn-danger"
        onClick={() => props.onEliminar(props.data)}
      >
        Eliminar
      </button>
    </div>
  );

  const localidadesColumnDefs = [
    {
      headerName: '',
      field: 'checked',
      width: 40,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      pinned: 'left'
    },
    { headerName: "Taller", field: "taller", width: 220 },
    { headerName: "Localidad", field: "localidad", width: 160 },
    { headerName: "CODIGO", field: "codigo", width: 140 },
    { headerName: "Nombre COMPAQ", field: "nombreCompaq", width: 180 },
    { headerName: "Nomenclatura", field: "nomenclatura", width: 180 },
    {
      headerName: "Acciones",
      field: "acciones",
      width: 140,
      cellRenderer: (params) => (
        <AccionesRenderer
          data={params.data}
          onEditar={handleEditarLocalidad}
          onEliminar={handleEliminarLocalidad}
        />
      )
    }
  ];

  const localidadesRowData = useMemo(() => {
    if (!Array.isArray(localidades)) return [];
    return localidades
      .filter(row => (row.taller || row.Taller) && (row.localidad || row.Localidad))
      .map(row => ({
        id: row.id,
        taller: row.taller || row.Taller || '',
        localidad: row.localidad || row.Localidad || '',
        codigo: row.codigo || row.CODIGO || '',
        nombreCompaq: row.nombreCompaq || row['NOMBRE COMPAQ'] || row.NOMBRE_COMPAQ || '',
        nomenclatura: row.nomenclatura || row.NOMENCLATURA || ''
      }));
  }, [localidades]);

  const localidadesRowDataFiltrada = useMemo(() => {
    const term = (localidadesSearchText || '').trim().toLowerCase();
    if (!term) return localidadesRowData;

    return localidadesRowData.filter((row) => {
      const values = [
        row?.taller,
        row?.localidad,
        row?.codigo,
        row?.nombreCompaq,
        row?.nomenclatura
      ];
      return values.some((value) => String(value ?? '').toLowerCase().includes(term));
    });
  }, [localidadesRowData, localidadesSearchText]);

  const codigosColumnDefs = useMemo(() => ([
    {
      headerName: '',
      field: 'select',
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      pinned: 'left',
      resizable: false,
      suppressMenu: true,
      cellClass: 'codigos-cell codigos-cell--checkbox'
    },
    {
      headerName: 'Nombre del producto',
      field: 'nombre',
      flex: 1.4,
      minWidth: 320,
      tooltipField: 'nombre',
      cellClass: 'codigos-cell codigos-cell--nombre'
    },
    {
      headerName: 'Código del producto',
      field: 'codigo',
      flex: 0.8,
      minWidth: 200,
      tooltipField: 'codigo',
      cellClass: 'codigos-cell codigos-cell--codigo'
    }
  ]), []);

  const deferredInlineCodigoQuery = useDeferredValue(inlineCodigoQuery);

  useEffect(() => {
    if (!codigosDrawerOpen) {
      inlineSearchAbortRef.current?.abort();
      inlineSearchAbortRef.current = null;
      setInlineCodigoResults([]);
      setInlineCodigosLoading(false);
      return;
    }

    const normalized = deferredInlineCodigoQuery.trim();
    const controller = new AbortController();
    inlineSearchAbortRef.current = controller;

    const params = new URLSearchParams({ limit: '12' });
    if (normalized) {
      params.set('q', normalized);
    }

    const requestUrl = `${API_BASE_URL}/api/codigos/buscar?${params.toString()}`;

    setInlineCodigosLoading(true);

    fetch(requestUrl, { signal: controller.signal })
      .then(response => {
        if (!response.ok) {
          throw new Error('No se pudo filtrar el catálogo');
        }
        return response.json();
      })
      .then(async data => {
        if (controller.signal.aborted) return;

        const registrosCatalogo = Array.isArray(data?.registros) ? data.registros : [];
        setInlineCodigoResults(registrosCatalogo);

        const debeBuscarHistorico =
          normalized &&
          normalized.length >= 4 &&
          registrosCatalogo.length < 6 &&
          Array.isArray(baseHistoricaRows) &&
          baseHistoricaRows.length > 0;

        if (!debeBuscarHistorico) return;

        const histResponse = await fetch(`${API_BASE_URL}/api/codigos/buscar-flex`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
          body: JSON.stringify({
            q: normalized,
            limit: 12,
            historico: baseHistoricaRows
          })
        });

        if (!histResponse.ok) return;
        const histData = await histResponse.json().catch(() => ({}));
        if (controller.signal.aborted) return;

        const registrosFlex = Array.isArray(histData?.registros) ? histData.registros : [];
        const combinados = [];
        const vistos = new Set();

        [...registrosCatalogo, ...registrosFlex].forEach(row => {
          const key = `${row?.codigo || ''}__${row?.nombre || ''}__${row?.fuente || ''}`;
          if (!row?.codigo || vistos.has(key)) return;
          vistos.add(key);
          combinados.push(row);
        });

        setInlineCodigoResults(combinados.slice(0, 12));
      })
      .catch(error => {
        if (controller.signal.aborted) return;
        console.error('Error al buscar códigos en línea:', error);
        setInlineCodigoResults([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setInlineCodigosLoading(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, [deferredInlineCodigoQuery, codigosDrawerOpen, baseHistoricaRows]);

  const codigosTimestampLabel = useMemo(() => {
    if (!codigosLastUpdated) return '';
    return codigosLastUpdated.toLocaleString('es-MX', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  }, [codigosLastUpdated]);

  return (
    <div className="codificar-page">
      <div className="codificar-shell">
        <div className="tab-container">
          <button
            type="button"
            className={`tab-button ${activeTab === 'principal' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('principal')}
          >
            Principal
          </button>
          {canUseOrdenesTab && (
            <button
              type="button"
              className={`tab-button ${activeTab === 'ordenes' ? 'is-active' : ''}`}
              onClick={() => setActiveTab('ordenes')}
            >
              Órdenes de proveedor
            </button>
          )}
          {canUseLocalidadesTab && (
            <>
              <button
                type="button"
                className={`tab-button ${activeTab === 'localidades' ? 'is-active' : ''}`}
                onClick={() => setActiveTab('localidades')}
              >
                Localidades
              </button>
              <button
                type="button"
                className={`tab-button ${activeTab === 'codigos' ? 'is-active' : ''}`}
                onClick={() => setActiveTab('codigos')}
              >
                Códigos
              </button>
            </>
          )}
        </div>

        {activeTab === 'principal' && (
          <>
            <div className="top-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowColumnPanel(v => !v)}
              >
                {showColumnPanel ? 'Ocultar opciones de columnas' : 'Mostrar opciones de columnas'}
              </button>
            </div>

            <div className="section-card action-bar">
              <div className="metrics">
                <span className="metric metric-total">Filas: {rowData.length}</span>
                <span className="metric metric-selected">Seleccionadas: {selectedCount}</span>
              </div>
              <div className="action-controls">
                <input
                  type="file"
                  accept=".xlsx, .xls"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => fileInputRef.current.click()}
                >
                  Subir Excel
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleLoadData}
                  disabled={!puedeCargar}
                >
                  Cargar información
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                >
                  Guardar información
                </button>
                {esSupervisor && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setMostrarValeManual(v => !v)}
                  >
                    {mostrarValeManual ? 'Cerrar VALE MANUAL' : 'Capturar VALE MANUAL'}
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleUndo}
                  disabled={!canUndo}
                >
                  Deshacer cambio
                </button>
                {esSupervisor && (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDeleteSelected}
                    disabled={selectedCount === 0}
                  >
                    Borrar seleccionados
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-autollenar-usados"
                  onClick={handleAutollenarUsadosOriginales}
                  disabled={autollenandoUsados}
                  title={autollenandoUsados ? 'Autollenando códigos, espera tantito.' : 'Autollenar usados/originales'}
                >
                  {autollenandoUsados ? 'Autollenando...' : 'Autollenar usados/originales'}
                </button>
                {autollenandoUsados && (
                  <span className="codificar-inline-status">
                    Autollenando códigos… espera tantito. No cierres ni presiones otra vez.
                  </span>
                )}
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={handleEnviarSeleccionados}
                  disabled={selectedCount === 0}
                >
                  Enviar seleccionados a base de datos
                </button>
              </div>
            </div>

            <ValeManualForm
              visible={mostrarValeManual}
              localidadesRows={localidadesRowData}
              guardando={guardandoValeManual}
              onSubmit={handleGuardarValeManual}
              onClose={() => setMostrarValeManual(false)}
            />

            <div className="inspector-catalog-row">
              <div
                className="section-card inspector-card"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                  minHeight: CODIFICAR_PANEL_HEIGHT,
                  maxHeight: CODIFICAR_PANEL_HEIGHT
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 15, color: '#111827' }}>
                      {inspectorHeader}
                    </div>
                    {inspectorCell ? (
                      <>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            fontSize: 13,
                            color: '#111827'
                          }}
                        >
                          <span>
                            <span style={{ fontWeight: 600, color: '#374151' }}>Pedido:</span>{' '}
                            {inspectorPedidoLabel || 'Sin valor'}
                          </span>
                          <span>
                            <span style={{ fontWeight: 600, color: '#374151' }}>Siniestro:</span>{' '}
                            {inspectorSiniestroLabel || 'Sin valor'}
                          </span>
                          <span>
                            <span style={{ fontWeight: 600, color: '#374151' }}>Modelo/Año:</span>{' '}
                            {inspectorModeloAnioLabel || 'Sin valor'}
                          </span>
                          <span>
                            <span style={{ fontWeight: 600, color: '#374151' }}>Origen:</span>{' '}
                            {inspectorOrigenLabel || 'Sin valor'}
                          </span>
                          <span>
                            <span style={{ fontWeight: 600, color: '#374151' }}>Taller:</span>{' '}
                            {inspectorTallerLabel || 'Sin valor'}
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                          {inspectorInstruction}
                        </div>
                      </>
                    ) : (
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                        Selecciona una celda para ver su contenido.
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={handleInspectorClear}
                    disabled={!inspectorCell}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    Limpiar selección
                  </button>
                </div>
                {inspectorCell ? (
                  <div
                    style={{
                      minHeight: 90,
                      maxHeight: 150,
                      borderRadius: 10,
                      border: '1px solid #d1d5db',
                      background: '#ffffff',
                      padding: 12,
                      fontSize: 12,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      color: '#111827',
                      overflowY: 'auto'
                    }}
                  >
                    {inspectorValueDisplay || '(vacío)'}
                  </div>
                ) : (
                  <div
                    style={{
                      minHeight: 90,
                      maxHeight: 150,
                      borderRadius: 10,
                      border: '1px dashed #d1d5db',
                      background: '#ffffff',
                      padding: 12,
                      fontSize: 12,
                      color: '#6b7280',
                      overflowY: 'auto'
                    }}
                  >
                    No hay una celda seleccionada.
                  </div>
                )}
                <div className="inspector-search">
                  <label htmlFor="inspector-search-input" className="inspector-search-label">
                    Buscar en la tabla principal
                  </label>
                  <div className="inspector-search-controls">
                    <input
                      id="inspector-search-input"
                      type="search"
                      className="input-control inspector-search-input"
                      placeholder="Filtra cualquier columna..."
                      value={gridQuickFilter}
                      onChange={e => handleGridQuickFilterChange(e.target.value)}
                    />
                    {gridQuickFilter && (
                      <button
                        type="button"
                        className="btn btn-inline btn-ghost inspector-search-clear"
                        onClick={() => handleGridQuickFilterChange('')}
                      >
                        Limpiar filtro
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div
                className={`section-card codigos-fixed-card ${codigosDrawerOpen ? '' : 'is-inactive'}`}
                style={{
                  height: CODIFICAR_PANEL_HEIGHT,
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <div className="codigos-drawer-header">
                  <div>
                    <h3>Catálogo fijo</h3>
                    {codigosDrawerRow ? (
                      <p className="codigos-drawer-subtitle">
                        {codigosDrawerRow?.ITEM || codigosDrawerRow?.NOMBRE || codigosDrawerRow?.PEDIDO || 'Fila'}
                      </p>
                    ) : (
                      <p className="codigos-drawer-subtitle">Sin fila activa</p>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn btn-icon"
                    onClick={handleCloseCodigosDrawer}
                    disabled={!codigosDrawerOpen}
                  >
                    ✕
                  </button>
                </div>
                {codigosDrawerOpen ? (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <div className="codigos-inline-filter">
                      <input
                        type="search"
                        className="input-control"
                        placeholder="Escribe para filtrar"
                        value={inlineCodigoQuery}
                        onChange={e => handleInlineCodigoInputChange(e.target.value)}
                        autoFocus={false}
                      />
                      <span className="tag-pill">
                        Coincidencias: {inlineCodigosLoading ? '...' : inlineCodigoResults.length.toLocaleString('es-MX')}
                      </span>
                    </div>
                    <div className="codigos-inline-hint">Haz clic sobre un resultado para copiarlo en la celda.</div>
                    <div className="codigos-inline-results" style={{ flex: 1, overflowY: 'auto' }}>
                      {inlineCodigosLoading ? (
                        <div className="codigos-inline-empty">Buscando coincidencias...</div>
                      ) : inlineCodigoResults.length > 0 ? (
                        inlineCodigoResults.map(row => (
                          <button
                            type="button"
                            key={row.id}
                            className="codigos-inline-result"
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => handleInlineCodigoPick(row)}
                          >
                            <span className="codigos-inline-nombre">
                              {row.nombre}
                              {row.fuente === 'historico' ? ' · Histórico' : ''}
                            </span>
                            <span className="codigos-inline-codigo">{row.codigo}</span>
                          </button>
                        ))
                      ) : (
                        <div className="codigos-inline-empty">No hay coincidencias para este filtro.</div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div
                    className="codigos-inline-empty codigos-inline-empty-muted"
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      padding: 16
                    }}
                  >
                    Selecciona una celda de la columna CODIGO para activar el catálogo fijo.
                  </div>
                )}
              </div>
            </div>

            {showColumnPanel && (
              <div className="section-card column-panel">
                <strong>Mostrar/Ocultar columnas:</strong>
                <div className="column-checkboxes">
                  {columnDefs
                    .filter(col => col.field !== 'checked')
                    .map(col => (
                      <label key={col.field} className="column-checkbox">
                        <input
                          type="checkbox"
                          checked={visibleColumns.includes(col.field)}
                          onChange={() => handleColumnToggle(col.field)}
                        />
                        {col.headerName}
                      </label>
                    ))}
                </div>
              </div>
            )}

            <div className="section-card grid-card">
              <div className="ag-theme-alpine ag-grid-wrapper">
                <AgGridReact
                  ref={gridRef}
                  columnDefs={filteredColumnDefs}
                  rowData={rowData}
                  quickFilterText={gridQuickFilter}
                  rowSelection="multiple"
                  singleClickEdit={true}
                  deltaRowDataMode={true}
                  getRowId={params => String(params.data.id)}
                  enableCellTextSelection={true}
                  suppressRowClickSelection={true}
                  domLayout="normal"
                  suppressHorizontalScroll={false}
                  suppressMovableColumns={true}
                  enableBrowserTooltips={true}
                  defaultColDef={{
                    resizable: true,
                    sortable: true,
                    filter: false,
                    minWidth: 40,
                    editable: false,
                  }}
                  headerHeight={32}
                  rowHeight={28}
                  onSelectionChanged={onSelectionChanged}
                  onCellValueChanged={handleCellValueChanged}
                  onCellEditingStarted={handleCellEditingStarted}
                  onCellEditingStopped={handleCellEditingStopped}
                  onCellClicked={handleCellClicked}
                  onCellFocused={handleCellFocused}
                  getRowClass={getRowClass}
                  stopEditingWhenCellsLoseFocus={!codigosDrawerOpen}
                  enterMovesDownAfterEdit={false}
                  enterMovesDown={false}
                  suppressClickEdit={false}
                  suppressKeyboardEvent={params => {
                    if (
                      params.editing &&
                      (params.event.key === 'Enter' || params.event.key === 'Tab') &&
                      params.column.getColId() !== "CODIGO"
                    ) {
                      return true;
                    }
                    return false;
                  }}
                />
              </div>
            </div>

            {esSupervisor && (
              <div className="section-card resumen-card">
                <button
                  type="button"
                  className="btn btn-primary resumen-toggle"
                  onClick={() => setMostrarResumen(prev => !prev)}
                >
                  {mostrarResumen ? 'Ocultar resumen de Siniestros y Pedidos' : 'Mostrar resumen de Siniestros y Pedidos'}
                </button>
                {mostrarResumen && (
                  <>
                    <h3 className="section-heading">Resumen de Siniestros y Pedidos</h3>
                    <div className="resumen-table-wrapper">
                      <table className="resumen-table">
                        <thead>
                          <tr>
                            <th>
                              SINIESTRO
                              <button
                                type="button"
                                className="btn-icon"
                                onClick={() => {
                                  const vistos = new Set();
                                  const texto = rowData
                                    .filter(row => {
                                      const clave = `${row.PEDIDO}|||${row.SINIESTRO}`;
                                      if (vistos.has(clave)) return false;
                                      vistos.add(clave);
                                      return true;
                                    })
                                    .map(row => (row.SINIESTRO ? row.SINIESTRO.substring(0, 11) : ''))
                                    .join('\n');
                                  navigator.clipboard.writeText(texto);
                                }}
                                title="Copiar toda la columna SINIESTRO"
                              >
                                ⧉
                              </button>
                            </th>
                            <th>
                              PEDIDO
                              <button
                                type="button"
                                className="btn-icon"
                                onClick={() => {
                                  const vistos = new Set();
                                  const texto = rowData
                                    .filter(row => {
                                      const clave = `${row.PEDIDO}|||${row.SINIESTRO}`;
                                      if (vistos.has(clave)) return false;
                                      vistos.add(clave);
                                      return true;
                                    })
                                    .map(row => (row.PEDIDO ? row.PEDIDO : ''))
                                    .join('\n');
                                  navigator.clipboard.writeText(texto);
                                }}
                                title="Copiar toda la columna PEDIDO"
                              >
                                ⧉
                              </button>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const vistos = new Set();
                            return rowData
                              .filter(row => {
                                const clave = `${row.PEDIDO}|||${row.SINIESTRO}`;
                                if (vistos.has(clave)) return false;
                                vistos.add(clave);
                                return true;
                              })
                              .map((row, idx) => (
                                <tr key={idx}>
                                  <td>
                                    <input
                                      type="text"
                                      value={row.SINIESTRO ? row.SINIESTRO.substring(0, 11) : ''}
                                      readOnly
                                      className="resumen-field"
                                      onClick={e => e.target.select()}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      value={row.PEDIDO ? row.PEDIDO : ''}
                                      readOnly
                                      className="resumen-field"
                                      onClick={e => e.target.select()}
                                    />
                                  </td>
                                </tr>
                              ));
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {activeTab === 'ordenes' && canUseOrdenesTab && (
          <div className="section-card">
            <h2 className="section-heading">Órdenes de Proveedor</h2>
            <p style={{ marginTop: 0, color: '#6b7280' }}>
              Relaciona pedido con orden de proveedor desde Codificar.
            </p>
            <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              {esSupervisor && (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteOrdenes}
                  disabled={ordenesSelectedCount === 0}
                >
                  Borrar seleccionados
                </button>
              )}
              <span className="tag-pill info">Seleccionadas: {ordenesSelectedCount}</span>
              <span className="tag-pill">Total filas: {filteredOrdenes.length}</span>
            </div>

            <div style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
              <input
                type="file"
                accept=".xlsx,.xls"
                ref={ordenesFileInputRef}
                style={{ display: 'none' }}
                onChange={handleOrdenesFileUpload}
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => ordenesFileInputRef.current?.click()}
              >
                Subir Excel de órdenes
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCargarOrdenesExcel}
                disabled={!ordenesPuedeCargar}
              >
                Cargar información
              </button>
              {ordenesExcelData.length > 0 && (
                <span className="tag-pill success">{ordenesExcelData.length} filas listas para cargar</span>
              )}
            </div>

            <div style={{ marginBottom: 10 }}>
              <input
                type="text"
                className="input-control"
                placeholder="Buscar pedido u orden..."
                value={searchOrdenText}
                onChange={(event) => setSearchOrdenText(event.target.value)}
                style={{ maxWidth: 320 }}
              />
            </div>

            <div className="ag-theme-alpine ag-grid-wrapper small-grid">
              <AgGridReact
                ref={ordenesGridRef}
                columnDefs={ORDENES_COLUMN_DEFS}
                rowData={filteredOrdenes}
                rowSelection="multiple"
                getRowId={(params) => (params?.data?.id ? String(params.data.id) : undefined)}
                domLayout="normal"
                suppressHorizontalScroll={false}
                suppressMovableColumns={true}
                enableBrowserTooltips={true}
                enableCellTextSelection={true}
                defaultColDef={{
                  resizable: true,
                  sortable: false,
                  filter: false,
                  minWidth: 60,
                  editable: false,
                }}
                headerHeight={32}
                rowHeight={28}
                onSelectionChanged={onOrdenSelectionChanged}
              />
            </div>
          </div>
        )}

        {activeTab === 'localidades' && canUseLocalidadesTab && (
          <div className="section-card localidades-card">
            <h2 className="section-heading">Localidades</h2>
            <div className="localidades-controls">
              <input
                type="file"
                accept=".xlsx, .xls"
                ref={localidadesFileInputRef}
                style={{ display: 'none' }}
                onChange={handleLocalidadesFileUpload}
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => localidadesFileInputRef.current.click()}
              >
                Subir Excel de localidades
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCargarLocalidades}
                disabled={localidadesExcel.length === 0}
              >
                Cargar información
              </button>
              {localidadesExcel.length > 0 && (
                <span className="tag-pill success">
                  {localidadesExcel.length} filas listas para cargar
                </span>
              )}
            </div>
            <div className="localidades-toolbar">
              <div className="localidades-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setAgregando(v => !v)}
                >
                  {agregando ? 'Cancelar' : 'Agregar manualmente'}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleEliminarLocalidadesSeleccionadas}
                  disabled={localidadesSeleccionadas.length === 0}
                >
                  Borrar seleccionados
                </button>
                <span className="tag-pill info">
                  Seleccionadas: {localidadesSeleccionadas.length}
                </span>
              </div>

              <div className="localidades-search-row">
                <input
                  type="text"
                  className="input-control localidades-search-input"
                  placeholder="Buscar en localidades..."
                  value={localidadesSearchText}
                  onChange={(e) => setLocalidadesSearchText(e.target.value)}
                />
                {localidadesSearchText && (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => setLocalidadesSearchText('')}
                  >
                    Limpiar
                  </button>
                )}
                <span className="tag-pill info">
                  Resultados: {localidadesRowDataFiltrada.length}
                </span>
              </div>

              {agregando && (
                <div className="form-inline">
                  <input
                    type="text"
                    placeholder="Taller"
                    value={nuevaLocalidad.taller}
                    onChange={e => setNuevaLocalidad({ ...nuevaLocalidad, taller: e.target.value })}
                    className="input-control"
                  />
                  <input
                    type="text"
                    placeholder="Localidad"
                    value={nuevaLocalidad.localidad}
                    onChange={e => setNuevaLocalidad({ ...nuevaLocalidad, localidad: e.target.value })}
                    className="input-control"
                  />
                  <input
                    type="text"
                    placeholder="Código"
                    value={nuevaLocalidad.codigo}
                    onChange={e => setNuevaLocalidad({ ...nuevaLocalidad, codigo: e.target.value })}
                    className="input-control"
                  />
                  <input
                    type="text"
                    placeholder="Nombre COMPAQ"
                    value={nuevaLocalidad.nombreCompaq}
                    onChange={e => setNuevaLocalidad({ ...nuevaLocalidad, nombreCompaq: e.target.value })}
                    className="input-control"
                  />
                  <input
                    type="text"
                    placeholder="Nomenclatura"
                    value={nuevaLocalidad.nomenclatura}
                    onChange={e => setNuevaLocalidad({ ...nuevaLocalidad, nomenclatura: e.target.value })}
                    className="input-control"
                  />
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleAgregarManual}
                  >
                    Guardar
                  </button>
                </div>
              )}
            </div>
            {editando && (
              <div className="form-inline editing-bar">
                <input
                  type="text"
                  placeholder="Taller"
                  value={editando.taller}
                  onChange={e => setEditando({ ...editando, taller: e.target.value })}
                  className="input-control"
                />
                <input
                  type="text"
                  placeholder="Localidad"
                  value={editando.localidad}
                  onChange={e => setEditando({ ...editando, localidad: e.target.value })}
                  className="input-control"
                />
                <input
                  type="text"
                  placeholder="Código"
                  value={editando.codigo || ''}
                  onChange={e => setEditando({ ...editando, codigo: e.target.value })}
                  className="input-control"
                />
                <input
                  type="text"
                  placeholder="Nombre COMPAQ"
                  value={editando.nombreCompaq || ''}
                  onChange={e => setEditando({ ...editando, nombreCompaq: e.target.value })}
                  className="input-control"
                />
                <input
                  type="text"
                  placeholder="Nomenclatura"
                  value={editando.nomenclatura || ''}
                  onChange={e => setEditando({ ...editando, nomenclatura: e.target.value })}
                  className="input-control"
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleGuardarEdicion}
                >
                  Guardar
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={handleCancelarEdicion}
                >
                  Cancelar
                </button>
              </div>
            )}
            <div className="ag-theme-alpine ag-grid-wrapper small-grid localidades-grid-wrapper">
              <AgGridReact
                ref={localidadesGridRef}
                columnDefs={localidadesColumnDefs}
                rowData={localidadesRowDataFiltrada}
                domLayout="normal"
                defaultColDef={{
                  resizable: true,
                  sortable: true,
                  filter: true,
                }}
                rowSelection="multiple"
                onSelectionChanged={onLocalidadesSelectionChanged}
                suppressRowClickSelection={true}
                headerHeight={32}
                rowHeight={28}
              />
            </div>
          </div>
        )}

        {activeTab === 'codigos' && canUseCodigosTab && (
          <div className="section-card codigos-card">
            <div className="codigos-header">
              <div>
                <h2 className="section-heading">Catálogo de códigos</h2>
                {(codigosFileName || codigosTimestampLabel) && (
                  <p className="codigos-note">
                    {codigosFileName && (
                      <>
                        Archivo: <strong>{codigosFileName}</strong>
                      </>
                    )}
                    {codigosTimestampLabel && (
                      <>
                        {codigosFileName ? ' • ' : ''}
                        Última sincronización {codigosTimestampLabel}
                      </>
                    )}
                  </p>
                )}
              </div>
              <div className="codigos-summary">
                <span>Total: {codigosTotal.toLocaleString('es-MX')}</span>
                <span>Coincidencias: {codigosFilteredTotal.toLocaleString('es-MX')}</span>
                <span>
                  Mostrando {codigosFilteredTotal ? `${codigosShowingFrom.toLocaleString('es-MX')} - ${codigosShowingTo.toLocaleString('es-MX')}` : '0'}
                </span>
              </div>
            </div>
            <div className="codigos-controls">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                ref={codigosFileInputRef}
                style={{ display: 'none' }}
                onChange={handleCodigosFileUpload}
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => codigosFileInputRef.current?.click()}
                disabled={codigosLoading}
              >
                {codigosLoading ? 'Procesando...' : 'Subir Excel de códigos'}
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={handleDownloadCodigosTemplate}
              >
                Descargar plantilla
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={handleClearCodigos}
                disabled={codigosLoading}
              >
                Limpiar datos
              </button>
              <input
                type="search"
                className="input-control codigos-search"
                placeholder="Buscar por nombre o código"
                value={codigoSearchInput}
                onChange={e => setCodigoSearchInput(e.target.value)}
              />
              <label className="codigos-checkbox">
                <input
                  type="checkbox"
                  checked={codigoExactMatch}
                  onChange={e => setCodigoExactMatch(e.target.checked)}
                />
                Coincidencia exacta
              </label>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteSelectedCodigos}
                disabled={!codigosSeleccionadas.length || codigosLoading}
              >
                Borrar seleccionados
              </button>
              <span className="tag-pill">
                Seleccionados: {codigosSeleccionadas.length}
              </span>
            </div>
            <div className="codigos-controls">
              <input
                type="text"
                className="input-control"
                placeholder="Código del producto"
                value={nuevoCodigoManual.codigo}
                onChange={e => setNuevoCodigoManual({ ...nuevoCodigoManual, codigo: e.target.value })}
                disabled={codigosLoading}
              />
              <input
                type="text"
                className="input-control"
                placeholder="Descripción del producto"
                value={nuevoCodigoManual.nombre}
                onChange={e => setNuevoCodigoManual({ ...nuevoCodigoManual, nombre: e.target.value })}
                disabled={codigosLoading}
              />
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAgregarCodigoManual}
                disabled={codigosLoading}
              >
                Agregar código
              </button>
            </div>
            <div className="codigos-pagination">
              <button
                type="button"
                className="btn btn-inline btn-secondary"
                onClick={() => setCodigosPage(prev => Math.max(prev - 1, 0))}
                disabled={!codigosCanGoPrev || codigosLoading}
              >
                Página anterior
              </button>
              <span>
                Página {codigosFilteredTotal ? codigosPage + 1 : 0} de {codigosPageCount.toLocaleString('es-MX')}
              </span>
              <button
                type="button"
                className="btn btn-inline btn-secondary"
                onClick={() => setCodigosPage(prev => prev + 1)}
                disabled={!codigosCanGoNext || codigosLoading}
              >
                Página siguiente
              </button>
            </div>
            {codigosError && <div className="codigos-error">{codigosError}</div>}
            {!codigosError && !codigosLoading && codigosTotal === 0 && !codigosHasSearch && (
              <div className="codigos-empty">No hay códigos almacenados todavía.</div>
            )}
            {!codigosError && !codigosLoading && codigosHasSearch && codigosFilteredTotal === 0 && (
              <div className="codigos-empty">No se encontraron coincidencias para la búsqueda.</div>
            )}
            {codigosRows.length > 0 && (
              <div className="ag-theme-alpine ag-grid-wrapper codigos-grid">
                <AgGridReact
                  ref={codigosGridRef}
                  columnDefs={codigosColumnDefs}
                  rowData={codigosRows}
                  domLayout="normal"
                  suppressMovableColumns={true}
                  enableCellTextSelection={true}
                  suppressRowClickSelection={true}
                  rowSelection="multiple"
                  rowMultiSelectWithClick={true}
                  animateRows={false}
                  headerHeight={40}
                  rowHeight={34}
                  getRowId={params => params.data.id}
                  onSelectionChanged={handleCodigosSelectionChanged}
                  defaultColDef={{
                    resizable: true,
                    sortable: true,
                    filter: false,
                    wrapText: true,
                    autoHeight: true,
                    suppressMenu: true
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const columnasBaseDatos = [
  "ASEGURADORA", "COTIZACION", "PEDIDO", "FECHA_COTIZACION", "FECHA_PEDIDO", "PROMESA_DE_ENTREGA",
  "FECHA_CONFIRMACION_DE_PIEZA", "FECHA_CANCELACION_DE_PIEZA", "FECHA_FACTURACION_DE_PIEZA",
  "FECHA_ENTREGA_DE_PIEZA", "FECHA_RECEPCION_DE_PIEZA", "DIAS_PROMESA", "DIAS_ENTREGADO1",
  "DIAS_ENTREGADO2", "DIAS_RECIBIDO1", "DIAS_RECIBIDO2", "SINIESTRO", "NOMBRE_COMERCIAL_TALLER",
  "CIUDAD_TALLER", "ESTADO_TALLER", "NOMBRE_CONTACTO", "TELEFONO", "EMAIL", "ARMADORA", "MODELO",
  "ANIO", "RFC_PROVEEDOR", "RAZON_SOCIAL_PROVEEDOR", "NOMBRE_COMERCIAL_PROVEEDOR", "COLUMNA1",
  "ESTADO_PROVEEDOR", "ITEM", "ORIGEN", "PRECIO", "ESTATUS", "BACK_ORDER", "CODIGO", "COSTO",
  "LOCALIDAD", "CHOFER", "COMPAQ", "OC", "NUEVO_ESTATUS", "ESTATUS_LOCAL", "ESTATUS_FORANEO", "ESTATUS2",
  "Tipo de siniestro", "VIN", "Placa de matrícula", "Municipio y colonia del taller",
  "Tipo de Valuación", "Proceso de creación", "Asignado automáticamente", "Fecha de facturación X",
  "Fecha de pago X", "Días de cotización", "ID de proveedor", "Calificación del proveedor",
  "Compañía de envío", "Nombre del almacén", "Número de pieza solicitada", "ID de pieza de la cotización",
  "Pieza del PIES", "Nombre de pieza en inventario", "Marca de la pieza", "Notas de la pieza",
  "Datos administrativos", "Vehículo en el piso", "Cotizado automáticamente", "Cantidad",
  "Precio antiguo", "Gastos de envío", "Gastos de devolución", "Precio total X"
];

const completarFila = (row) => {
  const nueva = {};
  columnasBaseDatos.forEach(col => {
    nueva[col] = row[col] !== undefined ? row[col] : "";
  });
  const aseguradora = String(nueva.ASEGURADORA ?? "").trim().toUpperCase();
  const pedido = String(nueva.PEDIDO ?? "").trim();
  const siniestro = String(nueva.SINIESTRO ?? "").trim();
  if (aseguradora.includes("MULTIASISTENCIA") && !pedido && siniestro) {
    nueva.PEDIDO = siniestro.slice(1);
  }
  return nueva;
};

export default CodificarPage;